import { BrandMark } from "./ui/BrandMark";
import { IntroLoadBar } from "./ui/IntroLoadBar";
import { StartHost } from "./ui/StartHost";
import type { OverviewFact, OverviewTopic } from "./ui/StartOverview";
import { COURSE_READINGS, localizedReading } from "./scenario/readingRegistry";
import { setContentLocale, setCourseOverlay, useTxVersion, tx, txr } from "./lib/tx";
import {
  subscribeLoadProgress,
  getLoadVersion,
  isSceneReady,
  summarizeLoad,
} from "./three/loadProgress";
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import type { CSSProperties } from "react";
import { Scene } from "./three/Scene";
import { Desktop } from "./devices/Desktop";
import { PolicyLibrary } from "./devices/PolicyLibrary";
import { BankStory } from "./devices/BankStory";
import { SendItSafely, type SendItGuide } from "./devices/SendItSafely";
import { MalwareDesk, type MalwareGuide } from "./devices/MalwareDesk";
import { Browser } from "./devices/Browser";
import { Mail } from "./devices/Mail";
import { EBanking } from "./devices/EBanking";
import { PhoneCall } from "./devices/PhoneCall";
import { VideoCall } from "./devices/VideoCall";
import { Intercom } from "./devices/Intercom";
import { TechRequest } from "./devices/TechRequest";
import { Tailgate } from "./devices/Tailgate";
import { Terminal } from "./devices/Terminal";
import { BreachBrief } from "./devices/BreachBrief";
import { UsbDevice } from "./devices/UsbDevice";
import { PrivacyHunt3D } from "./devices/PrivacyHunt3D";
import { PrivacySort, type PrivacyClassifyResult } from "./devices/PrivacySort";
import type { PrivacyItemPin } from "./three/PrivacyRoomProps";
import { SignIn } from "./devices/SignIn";
import { DeskLogin } from "./devices/DeskLogin";
import { MfaFatigue } from "./devices/MfaFatigue";
import { InboxTriage } from "./devices/InboxTriage";
import { BecVerify } from "./devices/BecVerify";
import { IncidentTicket } from "./devices/IncidentTicket";
import { IncidentReport } from "./devices/IncidentReport";
import { AiAssistant } from "./devices/AiAssistant";
import { AI_ASSISTANT_STAGE_GUIDE } from "./devices/ai-assistant/stageGuide";
import { UsbReveal } from "./devices/UsbReveal";
import { QuishingPhone } from "./devices/QuishingPhone";
import { DeepfakeGen } from "./devices/DeepfakeGen";
import { DeepfakeCall } from "./devices/DeepfakeCall";
import { ItCall } from "./devices/ItCall";
import { FileExplorer } from "./devices/FileExplorer";
import { RansomNote } from "./devices/RansomNote";
import { LeakSite } from "./devices/LeakSite";
import { Newscast } from "./devices/Newscast";
import { NewsExplainer } from "./devices/NewsExplainer";
import { SlideDeck } from "./devices/SlideDeck";
import { DecisionDrill } from "./devices/DecisionDrill";
import { SocialPost } from "./devices/SocialPost";
import { OsintRecon } from "./devices/OsintRecon";
import { MacroDoc } from "./devices/MacroDoc";
import { Exfil } from "./devices/Exfil";
import { PackList } from "./devices/PackList";
import { PackRoom, type PackResult } from "./devices/PackRoom";
import { WifiPicker } from "./devices/WifiPicker";
import { WifiKiosk } from "./devices/WifiKiosk";
import { ColleagueCall } from "./devices/ColleagueCall";
import { CafeCharge } from "./devices/CafeCharge";
import { ByodLaptop } from "./devices/ByodLaptop";
import { PublicPc, PublicPcFilm } from "./devices/PublicPc";
import { PersonalLaptop } from "./devices/PersonalLaptop";
import { ShoulderSurf } from "./devices/ShoulderSurf";
import { LostDevice } from "./devices/LostDevice";
import { ChargerChoice } from "./devices/ChargerChoice";
import { ByodGate } from "./devices/ByodGate";
import { IncidentTimeline } from "./devices/IncidentTimeline";
import { LiveVishingCall } from "./devices/LiveVishingCall";
import { RemoteSupportOverlay } from "./devices/RemoteSupportOverlay";
import { BarChat } from "./devices/BarChat";
import { BarLaptop } from "./devices/BarLaptop";
import { MitmReveal } from "./devices/MitmReveal";
import { TurkeyHunt } from "./devices/turkeyhunt/TurkeyHunt";
import { SpotFraud } from "./devices/spotfraud/SpotFraud";
import { Invaders } from "./devices/invaders/Invaders";
import { ArcadeTv } from "./devices/arcade/ArcadeTv";
import { HubPins } from "./devices/arcade/HubPins";
import type { ArcadeSpotPin } from "./three/ArcadeMarkers";
import { CocktailQrPhone } from "./devices/CocktailQrPhone";
import { PhotoStory } from "./devices/PhotoStory";
import { HackerZoom } from "./devices/HackerZoom";
import { EmailDraft } from "./devices/EmailDraft";
import { StoryEditor } from "./devices/StoryEditor";
import { PoolTablet } from "./devices/PoolTablet";
import { HolidayTablet } from "./devices/HolidayTablet";
import { HolidayBriefing } from "./devices/HolidayBriefing";
import { HolidayFakeHunt } from "./devices/HolidayFakeHunt";
import { HolidayRecon } from "./devices/HolidayRecon";
import { HolidayMeeting } from "./devices/HolidayMeeting";
import { RealOrAi } from "./devices/RealOrAi";
import { GuideTooltip } from "./ui/GuideTooltip";
import { ReplayBanner } from "./ui/ReplayBanner";
import { LookAroundTutorial } from "./ui/LookAroundTutorial";
import { CinemaScreen } from "./ui/CinemaScreen";
import { PcScreenOverlay } from "./ui/PcScreenOverlay";
import { CAM_TWEEN_MS } from "./three/FirstPersonControls";
import { openTvPlaybackGate, setGlobalTvDuck, stopAllVideoScreens } from "./three/VideoScreen";
import { preloadGltfUrls } from "./three/gltfPreload";
import { WalkControls } from "./ui/WalkControls";
import { RoomWaypointNav } from "./ui/RoomWaypointNav";
import { TeachOverlay } from "./ui/TeachOverlay";
import { Debrief } from "./ui/Debrief";
import { Library } from "./ui/Library";
import { IntroPage } from "./ui/IntroPage";
import { topicGlyph, topicLabel } from "./scenario/topics";
import { getHostConfig, hubBackHref, trackModule } from "./lib/hostConfig";
import { RiskMeter } from "./ui/RiskMeter";
import { ExposureMap } from "./ui/ExposureMap";
import { VoicePanel } from "./ui/VoicePanel";
import { QualityPrefs } from "./ui/QualityPrefs";
import { Consequence } from "./ui/Consequence";
import { TerminalDebrief } from "./ui/TerminalDebrief";
import { MfitChat } from "./devices/mfit/MfitChat";
import { MfitConsent } from "./devices/mfit/MfitConsent";
import { MfitCloudDash, dashMatch, recordedEvents } from "./devices/mfit/MfitCloudDash";
import { MfaPc } from "./devices/mfa/MfaPc";
import { MfaPhone } from "./devices/mfa/MfaPhone";
import { ClickFix } from "./devices/clickfix/ClickFix";
import { OneClick } from "./devices/oneclick/OneClick";
import { Invoice } from "./devices/invoice/Invoice";
import { DevDay } from "./devices/devday/DevDay";
import { DevDayMap } from "./devices/devday/map";
import type { DevDayConfig } from "./scenario/types";
import type { MfitChatConfig, MfitCloudDashConfig, MfitConsentConfig, MfitGuideText, MfaPcConfig, MfaPhoneConfig, ClickFixConfig, OneClickConfig, InvoiceConfig } from "./scenario/types";
import { RoomHotspots } from "./ui/RoomHotspots";
import { IntroGlyph } from "./ui/IntroIcons";
import { useSim, sliceStopIds, sliceStopsNext } from "./state/store";
import { audio } from "./lib/audio";
import { t, tf } from "./lib/uiStrings";
import { probeCapability, nextLowerTier, type Capability } from "./lib/capability";
import { loadScenario, hasExplicitScenario } from "./scenario/registry";
import {
  SUPPORTED_LOCALES,
  getActiveLocale,
  setActiveLocale,
  isLocaleAvailable,
  loadLocaleOverlay,
  applyLocaleOverlay,
  loadLocaleIndex,
  loadChromeOverlay,
} from "./lib/locale";
import type {
  BrowserConfig,
  ByodGateConfig,
  ByodLaptopConfig,
  CafeChargeConfig,
  ChargerChoiceConfig,
  DeepfakeCallConfig,
  DeepfakeGenConfig,
  DesktopConfig, PolicyPageConfig, BankStoryConfig,
  DeviceTier,
  EBankingConfig,
  EnvironmentSpec,
  FileExplorerConfig,
  IncidentTimelineConfig,
  IntercomConfig,
  ItCallConfig,
  LeakSiteConfig,
  LiveVishingConfig,
  RemoteSupportConfig,
  LostDeviceConfig,
  MailConfig,
  NewscastConfig,
  NewsExplainerConfig,
  SlideDeckConfig,
  DecisionDrillConfig,
  SocialPostConfig,
  OsintReconConfig,
  MacroDocConfig,
  ExfilConfig,
  PackListConfig,
  PhoneConfig,
  PublicCallConfig,
  PublicPcConfig,
  QuishingConfig,
  RansomNoteConfig,
  ScenarioSpec,
  ShoulderSurfConfig,
  TailgateConfig,
  TerminalConfig,
  BreachBriefConfig,
  UsbDeviceConfig,
  PrivacyHuntConfig,
  PrivacyClassifyConfig,
  SignInConfig,
  DeskLoginConfig,
  MfaFatigueConfig,
  InboxTriageConfig,
  BecVerifyConfig,
  IncidentTicketConfig,
  IncidentReportConfig,
  TechRequestConfig,
  UsbConfig,
  VideoCallConfig,
  WifiKioskConfig,
  WifiPickerConfig,
  PackItemSpec,
  AiAssistantConfig,
  PersonalLaptopConfig,
  BarChatConfig,
  BarLaptopConfig,
  MitmRevealConfig,
  TurkeyHuntConfig,
  SpotFraudConfig,
  InvadersConfig,
  ArcadeTvConfig,
  CocktailQrConfig,
  CocktailQrOption,
  ScreenBox,
  PhotoStoryConfig,
  HackerZoomConfig,
  EmailDraftConfig,
  StoryEditorConfig,
  PoolTabletConfig,
  HolidayTabletConfig,
  HolidayBriefingConfig,
  HolidayFakeHuntConfig,
  HolidayReconConfig,
  HolidayMeetingConfig,
  RealOrAiConfig,
} from "./scenario/types";

/** Device kinds whose DOM blends ONTO the 3D monitor/laptop screen — these get
 *  the "power on" gate. Physical-scene overlays (intercom/tech/usb/itcall) and
 *  cinematics (leaksite/newscast/ransomnote) render directly full-screen (a
 *  takeover, not a powered monitor app). fileexplorer DOES blend on the monitor. */
const SCREEN_KINDS = new Set(["desktop", "mail", "browser", "ebanking", "fileexplorer", "inboxtriage", "aiassistant", "desklogin", "personallaptop", "barlaptop", "pooltablet", "holidaytablet", "holidaymeeting", "holidayrecon", "bankstory", "sendit", "malwarepc", "mfitchat", "mfapc"]);

/** Device kinds that render their OWN visible dialogue/transcript UI, so the
 *  generic bottom `.cf-caption-bar` would just duplicate text already on
 *  screen. See the caption-bar render condition below. itcall/publiccall/
 *  techrequest have a live call transcript; packlist/cafecharge/lostdevice
 *  (the pin-pano family) render `config.body` VERBATIM in a persistent
 *  sidebar panel (see PackRoom.tsx/CafeCharge.tsx/LostDevice.tsx) while ALSO
 *  speaking that same string on mount -- without this entry the exact same
 *  sentence appeared twice at once (sidebar + bottom caption). The sidebar
 *  copy stays on screen the whole beat, so suppressing the transient caption
 *  loses no information for hearing-impaired learners. */
const HAS_OWN_DIALOGUE_UI = new Set([
  "turkeyhunt",
  "spotfraud",
  "invaders",
  "arcadetv",
  "itcall",
  "mfaphone",
  "publiccall",
  "techrequest",
  "packlist",
  "cafecharge",
  "lostdevice",
  "terminal",
  "breachbrief",
  "slidedeck",
  "barchat",
  "barlaptop",
  "mitmreveal",
  "cocktailqr",
  "photostory",
  "hackerzoom",
  "emaildraft",
  "storyeditor",
  "holidayrecon",
  "holidaybriefing",
  "holidayfakehunt",
  "holidaymeeting",
  "realorai",
  "mfitclouddash",
]);

/** Device kinds suppressed from the top-center VoicePanel because they render
 *  their own top-of-screen banner/caption chrome that a second top-center
 *  badge would visually collide with (`.ic-*`/`.dfc-banner`), on top of
 *  HAS_OWN_DIALOGUE_UI's own transcript UIs (no visual corner conflict there,
 *  but no need to duplicate either). */
const VOICE_PANEL_SUPPRESS_KINDS = new Set([...HAS_OWN_DIALOGUE_UI, "deepfakecall", "intercom", "livevishing", "remotesupport"]);

/** Per-internal-stage guide copy for the business-centre PC (PublicPc), since
 *  that device has several sub-steps inside ONE beat and a static beat.guide
 *  cannot track them. Every step MUST tell the learner exactly what to click. */
/** Same idea as PC_STAGE_GUIDE, for the BYOD tablet (ByodLaptop). */
const TABLET_STAGE_GUIDE: Record<string, { title: string; body: string }> = {
  table: { title: "Pick up the tablet", body: "Click the tablet on the table to pick it up." },
  off: { title: "Unlock the tablet", body: "Click the lock screen to unlock it." },
  home: { title: "Download the game", body: "Click Game Store, then download the game anyway." },
  store: { title: "Install the game", body: "Click Install to continue despite the warning signs." },
  permissions: {
    title: "Review the permissions",
    body: "Click \u201cAllow & install\u201d to see what an over-broad permission ask looks like.",
  },
  installing: { title: "Installing\u2026", body: "Watch what the install actually does." },
  installed: { title: "Open the game", body: "Click Open to launch the newly installed app." },
  tracking: { title: "A second permission ask", body: "Click Allow to see this separate tracking prompt." },
  run: { title: "Watch what happens", body: "The app is doing more than playing a puzzle." },
  result: { title: "See the consequence", body: "Read what went wrong, then continue." },
};

/** Same idea, for the cafe-charge pano (CafeCharge). The persistent top-right
 *  aside used to repeat this same copy in the SAME corner as the guide
 *  tooltip -- with the tooltip drawn on top, only the tail of the aside's
 *  note peeked out underneath (2026-07-15 "yellow text behind other text"
 *  report). Folding the note into the guide tooltip itself (single source
 *  of truth for this corner) and trimming the aside to just its kicker
 *  fixes the overlap instead of just repositioning it. */
const CAFECHARGE_STAGE_GUIDE: Record<string, { title: string; body: string }> = {
  risky: {
    title: "The Charger of Doom",
    body:
      "The real objects are visible: Max's own charger on the table, the wall AC outlet, and the public USB cable. No safe option is enabled yet \u2014 tap the public USB port to charge and see what actually happens. The safe alternatives unlock right after.",
  },
  safe: {
    title: "Charge it the safe way",
    body: "Now that you've seen what a bad cable does, tap Max's own charger or the wall outlet instead.",
  },
};

const PC_STAGE_GUIDE: Record<string, { title: string; body: string; actionLabel?: string; actionLabels?: string[] }> = {
  // film mode (PublicPcFilm, config.film); labels match PublicPcFilm's defaults
  film_off: { title: "Wake the PC", body: "Click to power on the PC.", actionLabel: "Click to power on the PC" },
  film_desktop: { title: "Open the browser", body: "Click Browser to open the security awareness portal.", actionLabel: "Browser" },
  film_browser: { title: "Loading the portal", body: "Watch the address of the awareness portal being typed." },
  film_login: { title: "Sign in", body: "Click Log in to sign in to the awareness portal with Max's work account.", actionLabel: "Log in" },
  film_page: { title: "Watch the film", body: "Click Watch the video. Or click Skip the video to go straight to the quiz.", actionLabels: ["Watch the video", "Skip the video"] },
  film_playing: { title: "Watch the film", body: "Watch it to the end, or click Skip the video to go to the quiz.", actionLabel: "Skip the video" },
  film_ended: { title: "The film has finished", body: "Click Continue to quiz.", actionLabel: "Continue to quiz" },
  off: { title: "Wake the PC", body: "Click the dark screen to power it on." },
  desktop: { title: "Open the browser", body: "Click the Browser icon to check work email." },
  browser: { title: "Loading the address", body: "Watch Max type the company webmail address." },
  login: { title: "Sign in as Max", body: "Click Log in to sign in with Max's saved password." },
  inbox: {
    title: "Decide how to leave",
    body: "Max's shuttle is called. Try \u201cJust close the tab\u201d first \u2014 the safe move is \u201cSign out\u201d.",
  },
  leaving: { title: "Watching what happens\u2026", body: "The tab closes \u2014 but is the session really gone?" },
  exposed: { title: "See the consequence", body: "Read what the next guest can now do, then continue." },
  safe_result: { title: "Session properly ended", body: "Signing out is what actually protects the next guest." },
};

/** Same idea, for the personal-laptop backstory device (PersonalLaptop). */
const LAPTOP_STAGE_GUIDE: Record<string, { title: string; body: string }> = {
  desktop: { title: "Connect to the internet", body: "Click the OpenVPN icon, top-left." },
  desktop_online: { title: "You're connected", body: "Now click Browser to go online." },
  briefing_desktop: {
    title: "Read the download briefing",
    body: "Open Download_Safety.pdf before you search. It explains how malicious sites get into search results.",
  },
  briefing_done: { title: "Open the browser", body: "Briefing complete. Click Browser to search the web." },
  vpn_popup: { title: "Connect the VPN", body: "Click Connect to reach the company network from home." },
  vpn_connecting: { title: "Connecting\u2026", body: "Watch the tunnel come up." },
  vpn_connected: { title: "You're on the network", body: "See what the VPN now reaches, then continue." },
  browser_home: { title: "Search the web", body: "Type a search and press Enter." },
  results: {
    title: "Pick a result",
    body: "Click the result outlined in the blinking grey frame below \u2014 it isn't what it claims to be.",
  },
  fake_site: { title: "A free game", body: "Download the game to play it." },
  downloading: { title: "Downloading\u2026", body: "This \u201clauncher\u201d is doing more than it says." },
  installing: { title: "Installing\u2026", body: "Almost ready to play." },
  game: { title: "Enjoy the game", body: "Give it a few seconds." },
  stealer: {
    title: "The invisible theft",
    body: "The shop still works. Compare the normal user view with the attacker-only stealer console.",
  },
  flicker: { title: "Something's wrong", body: "Watch closely." },
  black: { title: "\u2026", body: "" },
};

/** Same idea, for the inbox-triage device (InboxTriage) — the top-right
 *  guide otherwise stays frozen on the beat's static intro copy for the
 *  entire episode, drifting out of date once the tutorial ends and the
 *  learner is actually reviewing messages. */

const OSINT_STAGE_GUIDE: Record<string, { title: string; body: string; actionLabel?: string }> = {
  search: {
    title: "You are the attacker now",
    body: "You switch roles: you are the attacker. ReconHound is an OSINT tool that collects what employees publish themselves on LinkedIn, Google reviews and social media. First, list who works at Veltora. Click Search public records.",
    actionLabel: "Search public records",
  },
  results: {
    title: "The attacker's target list",
    body: "The tool found five Veltora employees with their roles and work email addresses, all from public sources. The attacker keeps them as possible targets. Click Save target list.",
    actionLabel: "Save target list",
  },
  saved: {
    title: "Finding the weak spot",
    body: "Now the attacker looks for the person who shares the most about their private life, because that makes the best bait. Marco posts reviews and holiday photos publicly. Click Deep-scan social profiles.",
    actionLabel: "Deep-scan social profiles",
  },
  scan: {
    title: "Reading Marco's public life",
    body: "The deep scan joins Marco's Google review and his public Instagram: his favourite restaurant, the owner's name, his family and his love of sailing. Wait for the profile.",
  },
  profile: {
    title: "Everything needed for a lure",
    body: "From a few public posts the attacker now knows exactly what Marco would love to hear. Click Build the spear-phish to see the email it turns into.",
    actionLabel: "Build the spear-phish",
  },
};

/** IT desk-phone call (ItCall): the guide follows the call's phase. */
const ITCALL_STAGE_GUIDE: Record<string, { title: string; body: string; actionLabel?: string }> = {
  ringing: { title: "Your phone is ringing", body: "It's the IT Service Desk calling. Click Answer.", actionLabel: "Answer" },
  connecting: { title: "Connecting", body: "The call is connecting." },
  talking: { title: "On the call", body: "Ben from IT is speaking. Listen to what he wants." },
  deciding: { title: "Your reply", body: "Choose what you say to Ben. Your options are on the phone." },
  closing: { title: "On the call", body: "Ben is wrapping up the call." },
};

function activateGuideAction(label: string) {
  const want = label.trim().toLowerCase();
  if (!want) return;
  const nodes = Array.from(document.querySelectorAll(".device-layer, .cs-screen")).flatMap((root) =>
    Array.from(root.querySelectorAll("button, a, [role='button']")),
  );
  const ranked = nodes
    .map((el) => {
      const text = (el.textContent || "").replace(/\s+/g, " ").trim().toLowerCase();
      const aria = (el.getAttribute("aria-label") || "").trim().toLowerCase();
      let score = 0;
      if (text === want || aria === want) score = 3;
      else if (text.includes(want) || aria.includes(want)) score = 1;
      if (score && el.classList.contains("blink")) score += 1;
      return { el, score };
    })
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score);
  const hit = ranked[0]?.el;
  if (hit instanceof HTMLElement) {
    hit.click();
    return;
  }
  window.dispatchEvent(new CustomEvent("cf-guide-action", { detail: label }));
}

function labelsFrom(entry?: { actionLabel?: string; actionLabels?: string[] }): string[] {
  if (!entry) return [];
  if (entry.actionLabels && entry.actionLabels.length) return entry.actionLabels;
  return entry.actionLabel ? [entry.actionLabel] : [];
}

function guideActionLabels(opts: {
  kind?: string;
  holidayStage?: string | null;
  osintStage?: string | null;
  barLaptopStage?: string | null;
  aiStage?: string | null;
  phonePhase?: string | null;
  beatLabel?: string;
  beatLabels?: string[];
}): string[] {
  if (opts.kind === "phone" && opts.phonePhase === "code-ready") {
    return ["Send code", "Hang up"];
  }
  if (opts.kind === "phone" && (opts.phonePhase === "talking" || opts.phonePhase === "awaiting")) {
    return ["Hang up"];
  }
  if (opts.kind === "holidaytablet" && opts.holidayStage) {
    const label = HOLIDAY_TABLET_STAGE_GUIDE[opts.holidayStage]?.actionLabel;
    return label ? [label] : [];
  }
  if (opts.kind === "aiassistant" && opts.aiStage) {
    const label = AI_ASSISTANT_STAGE_GUIDE[opts.aiStage]?.actionLabel;
    return label ? [label] : [];
  }
  if (opts.kind === "osintrecon" && opts.osintStage) {
    const label = OSINT_STAGE_GUIDE[opts.osintStage]?.actionLabel;
    return label ? [label] : [];
  }
  if (opts.kind === "barlaptop") {
    const fromStage = labelsFrom(opts.barLaptopStage ? BARLAPTOP_STAGE_GUIDE[opts.barLaptopStage] : undefined);
    if (fromStage.length) return fromStage;
  }
  if (opts.beatLabels && opts.beatLabels.length) return opts.beatLabels;
  return opts.beatLabel ? [opts.beatLabel] : [];
}

function hubReturnSummary(scenarioId: string, gaveCode: boolean): { title: string; paragraphs: string[] } {
  if (scenarioId === "phishing-office") {
    if (gaveCode) {
      return {
        title: "That code was the trap",
        paragraphs: [
          "You read a verification code to the caller. This was voice phishing: the email, the fake bank page, and the phone call were one attack.",
          "The voice can be fake. AI can clone a person's voice from only about three seconds of audio, so a caller who sounds like your bank, your boss, or a colleague may not be them. A real bank will never ask you to read out a code. Hang up and call the number on your card.",
        ],
      };
    }
    return {
      title: "Good call",
      paragraphs: [
        "You hung up instead of reading the code. That is the right move.",
        "The call was still voice phishing. The urgent email and the fake login page were there to make the voice feel real. AI can copy a person's voice from only about three seconds of audio, so sounding familiar is not proof. If a call asks for a code, a password, or a transfer, hang up and call back on a number you already trust.",
      ],
    };
  }
  if (scenarioId === "spear-phishing-osint") {
    return {
      title: "How the attack worked",
      paragraphs: [
        "Public posts about dinner and the holiday gave the attacker a picture of Marco. That picture became a personal prize email.",
        "Replying, opening the link, and enabling the document let them in. They then used his work login and sold what they took. Share less in public, treat surprise prizes as suspicious, and never enable content or click through a certificate warning to sign in.",
      ],
    };
  }
  if (scenarioId === "send-it-safely") {
    return {
      title: "The workshop files are sorted",
      paragraphs: [
        "You chose who could open each file and what they could do. A matching name was not enough, a new share did not cancel an older folder path, and the public flyer needed a link people can open.",
        "The quiz is on the terminal. Go back when you are ready.",
      ],
    };
  }
  if (scenarioId === "inside-the-bank") {
    return {
      title: "That is how the bank was opened",
      paragraphs: [
        "A leaked password with the year changed opened the mailbox and then the bank. Public facts from Facebook became more guesses. The long password was only lost because it was typed into the fake salary page.",
        "The quiz is on the terminal. Go back when you are ready.",
      ],
    };
  }
  return {
    title: "Back to the terminal",
    paragraphs: ["You have seen how this attack works. Use the button to return to the terminal."],
  };
}

const INBOX_STAGE_GUIDE: Record<string, { title: string; body: string }> = {
  tutorial: { title: "Learn the inbox", body: "Follow the on-screen walkthrough, then read the closing rule card before you start rating messages." },
  triaging: { title: "Investigate each message", body: "Check \u201CSender details\u201D and any link, then choose Report as phishing, Looks legitimate, or Verify before acting." },
  ready: { title: "Keep going, or submit", body: "You've reviewed enough to submit \u2014 reviewing the rest is still worth doing before you click Submit." },
  results: { title: "Review your results", body: "See how each message was scored, then continue." },
};

/** Same idea, for the bar-counter laptop (BarLaptop) — the beat's own static
 *  guide ("Sign in, then click the Wi-Fi icon...") otherwise stays frozen
 *  through sign-in, Wi-Fi, the look-alike Google pick, the SSL warning, AND
 *  the webmail sign-in, so the instruction goes stale the moment the learner
 *  finishes the very first step (2026-07-29 feedback). */
const BARLAPTOP_STAGE_GUIDE: Record<
  string,
  { title: string; body: string; tip?: string; actionLabel?: string; actionLabels?: string[] }
> = {
  lock: {
    title: "Counter laptop",
    body: "Sign in with the blinking field to unlock the laptop.",
    actionLabel: "Sign in",
  },
  wifi_hint: {
    title: "Connect to Wi-Fi",
    body: "Click the Wi-Fi icon in the taskbar to see nearby networks.",
    actionLabel: "Wi-Fi icon",
  },
  wifi_pick: {
    title: "Pick a network",
    body: "Tom wants to check his work email. The guest network needs the password from the menu; the free one is open and fast. Decide what Tom does.",
  },
  wifi_connect: {
    title: "Join the network",
    body: "Click Connect to join this network, or pick another one. Decide what Tom does.",
  },
  connect_browser: {
    title: "Open the browser",
    body: "Click the Browser icon on the desktop, or the Mail icon in the taskbar, to check the company webmail.",
    actionLabels: ["Browser", "Mail"],
  },
  browser_open: {
    title: "Open the browser",
    body: "Open the Browser or Mail icon to check the company webmail.",
    actionLabels: ["Browser", "Mail"],
  },
  google: {
    title: "Find the real site",
    body: "Pick the search result for mail.acmecorp-portal.com. That is the real company domain.",
    actionLabel: "mail.acmecorp-portal.com",
  },
  ssl: {
    title: "Certificate warning",
    body: "The browser says this connection isn't private. Open What does this warning mean? if you want to know more, then decide whether Tom continues.",
    actionLabels: ["What does this warning mean?"],
  },
  webmail: {
    title: "Sign in to webmail",
    body: "Copy the password hint, then click the blinking Sign in.",
    actionLabels: ["Copy", "Sign in"],
  },
};

/** Holiday Call: keep the Meet Elena briefing card open through the pool
 *  camera tour. Top-right GuideTooltip is reserved for instructions/voice,
 *  which start at the iPad Wi-Fi beat. */
const HOLIDAY_INTRO_NARRATION_BEATS = new Set([
  "b_intro",
  "b_pool_wide",
  "b_pool_kids",
  "b_pool_behind",
  "b_pool_close",
  "b_pool_shoulder",
  "b_pool_ipad",
]);

const HOLIDAY_TABLET_STAGE_GUIDE: Record<string, { title: string; body: string; tip?: string; actionLabel?: string }> = {
  lost: { title: "Elena needs a connection", body: "Tap the highlighted Wi-Fi settings button.", actionLabel: "Wi-Fi settings" },
  wifi: {
    title: "Choose the hotel network",
    body: "Tap the highlighted Hotel_Guest network. Notice that it has no lock.",
    actionLabel: "Hotel_Guest",
  },
  connecting: { title: "Connecting", body: "The iPad is joining Hotel_Guest." },
  wifi_risk: { title: "Which network is real?", body: "A rogue access point can copy a hotel name. Elena cannot tell them apart. How would you notice?", actionLabel: "How would you notice?" },
  wifi_ssl: { title: "Open a trusted site", body: "Tap Next on Gmail. A rogue network often shows up as a certificate warning, not in the Wi-Fi name.", actionLabel: "Next" },
  wifi_ssl_warn: { title: "Optional: inspect the warning", body: "Tap the red lock, Not secure, or Advanced to see how a browser reports a fake certificate. Or continue — you do not have to inspect it.", actionLabel: "Continue" },
  wifi_ssl_lock: { title: "Open the certificate", body: "Tap Certificate is not valid and read who issued it.", actionLabel: "Certificate is not valid" },
  wifi_ssl_cert: { title: "Not Google", body: "Issued to mail.google.com, issued by CN=localhost — a captive-portal CA, not Google Trust Services. Close this, then open Advanced.", actionLabel: "I see — this is not Google" },
  wifi_ssl_advanced: { title: "Advanced is hidden on purpose", body: "Chrome hides the unsafe continue behind Advanced so you pause. Open it.", actionLabel: "Advanced" },
  wifi_ssl_proceed: { title: "Proceed is unsafe", body: "This is the click a captive portal wants. You already know the issuer is not Google.", actionLabel: "Proceed to mail.google.com (unsafe)" },
  connected: { title: "Back to Elena's iPad", body: "Tap Return to iPad.", actionLabel: "Return to iPad" },
  hunt_open: { title: "Hunt the Rogue", body: "Elena is curious which Wi-Fi signal was genuine. She has a Wi-Fi Radar tool that can locate nearby access points. Open it to investigate where each network is coming from.", actionLabel: "Wi-Fi Radar" },
  hunt_map: { title: "Find the rogue", body: "Anyone can start a hotspot from a phone or laptop and give it any network name. Signal strength and location can provide clues. Here, the scanner shows two GrandHotel_WiFi signals: one from hotel infrastructure and one from a guest laptop beside the pool. Tap the source that should not be broadcasting the official hotel network." },
  hunt_teach: { title: "Read the result", body: "The pool laptop copied the hotel name. Stay on this screen until you have read it, then continue.", actionLabel: "Continue" },
  rogue_lab_hotspot: { title: "Copy the trusted name", body: "In this controlled simulation, rename the laptop hotspot GrandHotel_WiFi and turn it on." },
  rogue_lab_browser: { title: "Get the analysis tool", body: "Review what NetworkMiner can—and cannot—see, then use the simulated official download." },
  rogue_lab_download: { title: "Verify the source", body: "The training lab verifies that the download came from the official NETRESEC website." },
  rogue_lab_install: { title: "Install and launch", body: "Install the network-analysis tool, then launch its live capture view." },
  rogue_lab_capture: { title: "Watch the traffic", body: "Compare encrypted HTTPS sessions with deliberately insecure lab services that expose plaintext credentials." },
  desktop: { title: "Open Mail", body: "Click the highlighted Mail app on Elena's company iPad.", actionLabel: "Mail" },
  inbox: { title: "Open the unread email", body: "Tap the highlighted LinkedIn notification.", actionLabel: "LinkedIn notification" },
  mail: { title: "Open Daniel's profile", body: "Tap View profile.", actionLabel: "View profile" },
  profile: { title: "Connection request", body: "Open every tab of Daniel's profile, then decide: accept the connection, or ignore it." },
  ignored_note: { title: "You left the request", body: "Now see what would have happened if Elena had accepted.", actionLabel: "See what would have happened" },
  messages: { title: "A new LinkedIn message", body: "Tap the blinking Messages control.", actionLabel: "Messages control" },
  conversation: { title: "Read Daniel's message", body: "He already knows Thomas and CyberSec Europe. Then see what that message is for.", actionLabel: "What does this message mean?" },
  payoff: { title: "Accepting is a trust decision", body: "If you do not genuinely know the person, pause before accepting. A convincing profile may belong to a scammer. Continue to the game and practise spotting warning signs in four fake profiles.", actionLabel: "Start the fake-profile game" },
  reveal: { title: "What Daniel really was", body: "A detailed profile, long work history, articles and hundreds of connections can all be manufactured or cultivated. The strongest protection is not how complete the profile looks—it is whether you genuinely know the person and can verify them independently." },
  home: { title: "Real or not?", body: "Tap the Real or not? icon on Elena's home screen. It is a short game: which people are real?", actionLabel: "Real or not?" },
};

function glueOverlayStyle(box: ScreenBox, insetFrac: number): CSSProperties {
  const ix = box.widthPct * insetFrac;
  const iy = box.heightPct * (insetFrac + 0.03);
  return {
    position: "absolute",
    left: `${box.leftPct + ix}%`,
    top: `${box.topPct + iy}%`,
    width: `${Math.max(1, box.widthPct - ix * 2)}%`,
    height: `${Math.max(1, box.heightPct - iy * 2)}%`,
    right: "auto",
    bottom: "auto",
  };
}

function gltfLoaderTitle(scenarioId?: string, procedural?: string) {
  if (procedural === "holiday_beach") return "Arriving at the beach bar…";
  if (procedural === "hacker_room") return "Entering the operator workspace…";
  if (procedural === "pool_walkable" || scenarioId === "holiday-call") return "Arriving at the pool…";
  return "Entering the office…";
}

/** Render `body` with an optional `phrase` accented via `.intro-hl`. Splits on
 *  the first occurrence only (case-insensitive) so the intro briefing can lift
 *  one key phrase (e.g. "Project Horizon") without any markup in the scenario. */
function renderHighlighted(body: string, phrase?: string) {
  if (!phrase) return body;
  const idx = body.toLowerCase().indexOf(phrase.toLowerCase());
  if (idx < 0) return body;
  return (
    <>
      {body.slice(0, idx)}
      <span className="intro-hl">{body.slice(idx, idx + phrase.length)}</span>
      {body.slice(idx + phrase.length)}
    </>
  );
}

/** Right-side glyph for the "Mission" intro card. Same stroke weight/viewbox
 *  family so a new icon always feels native to the design; pick the closest
 *  theme (or add a new case) rather than reusing "folder" for everything. */
function renderIntroIcon(icon?: string) {
  const common = { viewBox: "0 0 120 96", fill: "none" as const, className: "intro-folder" };
  switch (icon) {
    case "door":
      return (
        <svg {...common}>
          <rect x="34" y="10" width="42" height="76" rx="3" stroke="currentColor" strokeWidth="2.2" />
          <line x1="34" y1="10" x2="34" y2="86" stroke="currentColor" strokeWidth="2.2" />
          <circle cx="66" cy="48" r="2.4" fill="currentColor" />
          <path d="M76 14c10 4 18 12 22 24" stroke="currentColor" strokeWidth="2" strokeDasharray="3 5" />
          <circle cx="100" cy="52" r="10" stroke="currentColor" strokeWidth="2.2" />
          <path d="M95 52a5 5 0 0 1 10 0" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    case "package":
      return (
        <svg {...common}>
          <path d="M60 16 20 32v38l40 16 40-16V32L60 16Z" stroke="currentColor" strokeWidth="2.2" />
          <path d="M20 32l40 16 40-16M60 48v38" stroke="currentColor" strokeWidth="2.2" />
          <path d="M40 24l40 16" stroke="currentColor" strokeWidth="1.6" opacity="0.6" />
          <rect x="52" y="54" width="16" height="12" rx="2" fill="currentColor" opacity="0.85" />
        </svg>
      );
    case "desk":
      return (
        <svg {...common}>
          <rect x="26" y="18" width="56" height="38" rx="3" stroke="currentColor" strokeWidth="2.2" />
          <line x1="54" y1="56" x2="54" y2="64" stroke="currentColor" strokeWidth="2.2" />
          <line x1="40" y1="64" x2="68" y2="64" stroke="currentColor" strokeWidth="2.2" />
          <rect x="30" y="70" width="60" height="4" rx="2" fill="currentColor" opacity="0.5" />
          <path d="M46 34a8 6 0 0 1 16 0" stroke="currentColor" strokeWidth="2" opacity="0.9" />
          <rect x="44" y="34" width="20" height="14" rx="2.4" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    case "eye":
      return (
        <svg {...common}>
          <path d="M18 48c10-16 26-24 42-24s32 8 42 24c-10 16-26 24-42 24S28 64 18 48Z" stroke="currentColor" strokeWidth="2.2" />
          <circle cx="60" cy="48" r="13" stroke="currentColor" strokeWidth="2.2" />
          <circle cx="60" cy="48" r="4" fill="currentColor" />
        </svg>
      );
    case "shield":
      return (
        <svg {...common}>
          <path d="M60 12 92 24v26c0 20-14 30-32 34-18-4-32-14-32-34V24l32-12Z" stroke="currentColor" strokeWidth="2.2" />
          <path d="M46 48l10 10 18-20" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "folder":
    default:
      return (
        <svg {...common}>
          <path
            d="M8 20a6 6 0 0 1 6-6h30l8 10h50a6 6 0 0 1 6 6v50a6 6 0 0 1-6 6H14a6 6 0 0 1-6-6V20Z"
            stroke="currentColor"
            strokeWidth="2.2"
          />
          <rect x="48" y="44" width="24" height="20" rx="3" stroke="currentColor" strokeWidth="2.2" />
          <path d="M52 44v-5a8 8 0 0 1 16 0v5" stroke="currentColor" strokeWidth="2.2" />
          <circle cx="60" cy="53" r="2.4" fill="currentColor" />
        </svg>
      );
  }
}

export function App() {
  const sim = useSim();
  const scenario = sim.scenario;
  const beat = sim.currentBeat();
  const moduleTrackSeen = useRef<Set<string>>(new Set());

  const takeSafeChoice = () => {
    const choice = beat?.safeChoice;
    if (!beat || !choice) return;
    audio.cancelSpeech();
    sim.recordOutcome({
      type: "refusal",
      beatId: beat.id,
      eventId: choice.eventId,
      evidence: choice.evidence ?? choice.label,
    });
    sim.advance("safe");
  };

  useEffect(() => {
    if (scenario?.id !== "pc-to-darkweb" || !beat) return;
    const eventByBeat: Record<string, { module: string; event: "opened" | "completed" | "finished" }> = {
      b_intro: { module: "course", event: "opened" },
      b_deck_how_title: { module: "teaching_how_credentials_are_stolen", event: "opened" },
      b_done_deck_how: { module: "teaching_how_credentials_are_stolen", event: "completed" },
      b_deck_value_title: { module: "teaching_dark_web_value", event: "opened" },
      b_done_deck_value: { module: "teaching_dark_web_value", event: "completed" },
      b_phishing_title: { module: "lab_phishing", event: "opened" },
      b_done_phishing: { module: "lab_phishing", event: "completed" },
      b_download_title: { module: "lab_malicious_download", event: "opened" },
      b_done_download: { module: "lab_malicious_download", event: "completed" },
      b_wifi_title: { module: "lab_rogue_wifi", event: "opened" },
      b_done_wifi: { module: "lab_rogue_wifi", event: "completed" },
      b_usb_title: { module: "lab_badusb", event: "opened" },
      b_done_usb: { module: "lab_badusb", event: "completed" },
      b_quiz: { module: "course", event: "finished" },
    };
    const hit = eventByBeat[beat.id];
    if (!hit) return;
    const key = `${hit.module}:${hit.event}`;
    if (moduleTrackSeen.current.has(key)) return;
    moduleTrackSeen.current.add(key);
    trackModule(hit.module, hit.event);
  }, [scenario?.id, beat?.id]);

  const [cap, setCap] = useState<Capability | null>(null);
  // Effective tier can drop below the probed tier at runtime (FPS watchdog).
  const [tier, setTier] = useState<DeviceTier | null>(null);
  const [cameraWaypointId, setCameraWaypointId] = useState<string | null>(null);
  // Active 3D environment id (multi-scene courses CUT between rooms). null = base.
  const [activeEnvId, setActiveEnvId] = useState<string | null>(null);
  /** Brief black fade when hopping room waypoints (office-explore). */
  const [roomFade, setRoomFade] = useState(false);
  const [soundOn, setSoundOn] = useState<boolean>(audio.isEnabled());
  // Music/ambient bed on/off — independent of voice (setSoundOn). Toggled from
  // the persistent bottom control unit during play.
  const [musicOn, setMusicOn] = useState<boolean>(audio.isMusicEnabled());
  const [voiceOn, setVoiceOn] = useState<boolean>(audio.isVoiceEnabled());
  // Manual re-open of the navigation tutorial from the bottom "?" help button.
  const [showTutorial, setShowTutorial] = useState(false);
  // The on-screen apps (laptop/monitor) only render once the learner has powered
  // the screen ON. The power button is the deterministic "we are zoomed in" gate:
  // it appears only on a `device` beat (which the fixed camera flow guarantees is
  // AFTER the zoom completes), and a click is proof the learner is at the desk.
  const [screenOn, setScreenOn] = useState<boolean>(false);
  // Breach-course kiosk: after the first power-on, spoke routes return to an
  // already-live hub instead of replaying its narration and power gate.
  const [breachBriefSeen, setBreachBriefSeen] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("resume") === "1";
    } catch {
      return false;
    }
  });
  // Build-time voice-capture mode (?capture=1): the ops/prerender-voice.mjs
  // driver walks every beat to enumerate scripted lines (via the audio.ts
  // capture hook). It renders NO 3D <Scene> so it never needs a GPU/glTF/splat
  // to load headlessly — the device DOM overlays (which emit the spoken lines)
  // are pure DOM and mount from the beat state alone. Inert in production.
  const captureMode = useRef<boolean>(
    (() => {
      try {
        return new URLSearchParams(window.location.search).get("capture") === "1";
      } catch {
        return false;
      }
    })(),
  ).current;
  // Learner Inspect mode (OrbitControls around current waypoint).
  const [inspectMode, setInspectMode] = useState(false);
  // Learner-toggled free WASD exploration on an otherwise guided course
  // (only offered when `scenario.allowFreeExplore` is set).
  const [freeExplore, setFreeExplore] = useState<boolean>(() => {
    try {
      return localStorage.getItem("cf_sim_move_mode") === "free";
    } catch {
      return false;
    }
  });
  const chooseMoveMode = (free: boolean) => {
    setFreeExplore(free);
    try {
      localStorage.setItem("cf_sim_move_mode", free ? "free" : "guided");
    } catch {
      /* ignore */
    }
  };
  const [inspectedBeatIds, setInspectedBeatIds] = useState<Set<string>>(() => new Set());
  const [packPinScreens, setPackPinScreens] = useState<
    { id: string; xPct: number; yPct: number; visible: boolean }[]
  >([]);
  const [tutorialDismissed, setTutorialDismissed] = useState(false);
  // Walkable desk-PC: sticky-note screen position + whether the 2D zoom is open.
  const [deskPin, setDeskPin] = useState<{ xPct: number; yPct: number; visible: boolean; distance: number }>({
    xPct: 0,
    yPct: 0,
    visible: false,
    distance: 999,
  });
  const [deskPcOpen, setDeskPcOpen] = useState(false);
  // Episode 10 reuses the SAME Desk A laptop already rendered by
  // PrivacyRoomProps (no second/duplicate laptop) as "your own laptop":
  // sticky note on its bezel, same "click the real note to power on"
  // pattern as deskPin/oua_desk.
  const [loungePin, setLoungePin] = useState<{ xPct: number; yPct: number; visible: boolean; distance: number }>({
    xPct: 0,
    yPct: 0,
    visible: false,
    distance: 999,
  });
  // Bar Wi-Fi game: invisible click-prompt anchor ("Order a drink" near the
  // bartender) that ends the free-roam `b_walk` beat — same projected-pin
  // pattern as loungePin/deskPin, reported by three/CounterHotspot.tsx.
  const [arcadePins, setArcadePins] = useState<Record<string, ArcadeSpotPin>>({});
  const [counterPin, setCounterPin] = useState<{ xPct: number; yPct: number; visible: boolean; distance: number }>({
    xPct: 0,
    yPct: 0,
    visible: false,
    distance: 999,
  });
  /** Which screen the desk-PC overlay is on ("login" | "desktop") — drives the
   *  top-right task notes (copyable password during login, next steps after). */
  const [deskPcStage, setDeskPcStage] = useState<"login" | "desktop">("login");
  // Storyboard: task 1 = sign in + open the browser; task 2 = go to the
  // conference room and switch on the TV. tvPin/tvOn drive the second task.
  const [task1Done, setTask1Done] = useState(false);
  const [task2Done, setTask2Done] = useState(false);
  const [tvOn, setTvOn] = useState(false);
  // Reception welcome-kiosk terminal: screen-space pin only, for now (step 1
  // of the office-under-attack rebuild = just get the modelled 3D object
  // rendering correctly on the counter; click/overlay wiring is a later step).
  const [, setTerminalPin] = useState<{ xPct: number; yPct: number; visible: boolean; distance: number }>({
    xPct: 0,
    yPct: 0,
    visible: false,
    distance: 999,
  });
  // Wall-mounted entry intercom: screen-space pin, used only to project the 3D
  // panel position (calibration/overlay hook). The visitor quest itself still
  // runs through the Tailgate device overlay on its beat.
  const [, setIntercomPin] = useState<{ xPct: number; yPct: number; visible: boolean; distance: number }>({
    xPct: 0,
    yPct: 0,
    visible: false,
    distance: 999,
  });
  // Scene-1 gate: has the learner switched ON the reception welcome terminal?
  // While an environment HAS a receptionTerminal and it's still off, the desk
  // PC / sticky-note hotspot stays locked (can't be clicked or interacted with).
  const [terminalOn, setTerminalOn] = useState(false);
  // Episode 2's desk package: screen-space pin for the "Click to open" sticky
  // note (DeskPackage.tsx, a real 3D prop, mirrors the terminal/intercom
  // pattern) + whether the learner has already opened it. Once opened, the
  // full UsbDevice choice overlay mounts; the box itself stays visible on the
  // desk (it never disappears — it's a permanent prop, not a 2D card).
  const [packagePin, setPackagePin] = useState<{ xPct: number; yPct: number; visible: boolean; distance: number }>({
    xPct: 0,
    yPct: 0,
    visible: false,
    distance: 999,
  });
  const [packageOpened, setPackageOpened] = useState(false);
  // Episode 3 ("Office privacy"): live screen-space pins for the ten 3D
  // document-standee props (`environment.privacyRoom.items`), reported every
  // frame by `PrivacyRoomProps` while the learner walks the lounge in
  // free-roam (`b_privacy_hunt.forceFreeRoam`). Which ones have been clicked
  // is tracked here (not inside the device overlay) so a re-render of the
  // beat (e.g. tutorial dismiss) never loses progress.
  const [privacyPins, setPrivacyPins] = useState<PrivacyItemPin[]>([]);
  const [privacyFoundIds, setPrivacyFoundIds] = useState<Set<string>>(() => new Set());
  // Reset to "closed" on every fresh arrival at the decision beat (harmless
  // no-op today since there's no rewind edge back to it, but mirrors the
  // terminal/screen-lock gating pattern in case a future retry loop needs it).
  useEffect(() => {
    if (beat?.id === "b_usb_decision") setPackageOpened(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beat?.id]);
  const [tvPin, setTvPin] = useState<{ xPct: number; yPct: number; visible: boolean; distance: number }>({
    xPct: 0,
    yPct: 0,
    visible: false,
    distance: 999,
  });
  const [caption, setCaption] = useState<string | null>(null);
  const [pcStage, setPcStage] = useState<string | null>(null);
  const [itcPhase, setItcPhase] = useState<string | null>(null);
  // Send It Safely: the desktop tells the top-right guide which task and step it is on.
  const [senditGuide, setSenditGuide] = useState<SendItGuide | null>(null);
  const [malwareGuide, setMalwareGuide] = useState<MalwareGuide | null>(null);
  // The Message From IT devices (mfit*) report their top-right guide text.
  const [mfitGuide, setMfitGuide] = useState<MfitGuideText | null>(null);
  // Beat.fadeCut: black overlay that fades out over a hard environment cut.
  const [fadeCutKey, setFadeCutKey] = useState<string | null>(null);
  const [tabletStage, setTabletStage] = useState<string | null>(null);
  const [cafeStage, setCafeStage] = useState<string | null>(null);
  const [laptopStage, setLaptopStage] = useState<string | null>(null);
  const [inboxStage, setInboxStage] = useState<string | null>(null);
  const [osintStage, setOsintStage] = useState<string | null>(null);
  const [phonePhase, setPhonePhase] = useState<string | null>(null);
  const [awareStage, setAwareStage] = useState<"poster" | "playing" | "ended" | null>(null);
  const [aiStage, setAiStage] = useState<string | null>(null);
  const [barLaptopStage, setBarLaptopStage] = useState<string | null>(null);
  const [holidayStage, setHolidayStage] = useState<string | null>(null);
  const [loadedGltfUrls, setLoadedGltfUrls] = useState<Set<string>>(() => new Set());
  const [mitmLineIdx, setMitmLineIdx] = useState<number>(0);
  // Projected screen-space box for a world-space `environment.screenQuad`
  // (GltfScreenOverlay). Used to glue the hacker MITM reveal onto the CRT
  // monitor mesh instead of hand-tuned per-viewport CSS.
  const [screenBox, setScreenBox] = useState<ScreenBox | null>(null);
  const [packFeedback, setPackFeedback] = useState<PackResult | null>(null);
  // Tracks which camera beat we've already started moving for, so the effect
  // re-running (when cameraWaypointId changes) can't mistakenly fast-advance.
  const handledCamBeat = useRef<string | null>(null);
  // Tracks the beat we last retargeted to the privacy-hunt look-only camera
  // (see `wp_privacy_lookonly` below), so re-renders don't re-trigger it.
  const lookOnlyCamSet = useRef<string | null>(null);
  // With no ?scenario / ?spec the root shows the course library instead.
  const [showLibrary] = useState<boolean>(() => !hasExplicitScenario());
  // The pristine, un-overlaid English scenario as loaded from the registry —
  // kept around so the intro-card language selector can re-localize from a
  // clean source every time instead of layering overlays on overlays.
  const baseScenarioRef = useRef<ScenarioSpec | null>(null);
  const [uiLocale, setUiLocale] = useState<string>(() => getActiveLocale());
  // Re-render the whole player when a chrome or course overlay arrives.
  useTxVersion();

  // Low-resolution advisory (Change 6): shown ONCE, only on the very first
  // screen, so learners on a small/narrow window know some 3D-mapped-screen
  // content and HUD cards are tuned for >=1440px wide and may look cramped
  // below it. Advisory only — never blocks play. Dismiss is per-session
  // (sessionStorage) so it doesn't nag on every reload while testing/using.
  const [lowResDismissed, setLowResDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem("cf_sim_lowres_dismissed") === "1";
    } catch {
      return false;
    }
  });
  const [viewportW, setViewportW] = useState<number>(() => (typeof window !== "undefined" ? window.innerWidth : 1920));
  useEffect(() => {
    const onResize = () => setViewportW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const dismissLowResWarning = () => {
    setLowResDismissed(true);
    try {
      sessionStorage.setItem("cf_sim_lowres_dismissed", "1");
    } catch {
      /* private mode / storage disabled — dismissal just won't persist across reloads */
    }
  };

  // Boot: probe device, load the selected scenario (registry / remote spec).
  useEffect(() => {
    audio.installAutoUnlock();
    (window as unknown as Record<string, unknown>).__cfAudio = audio;
    return audio.subscribeCaption(setCaption);
  }, []);

  useEffect(() => {
    if (showLibrary) return;
    let alive = true;
    Promise.all([probeCapability(), loadScenario()]).then(async ([c, spec]) => {
      if (!alive) return;
      baseScenarioRef.current = spec;
      setCap(c);
      setTier(c.tier);
      // Apply a previously-chosen (URL / storage / host) locale before the
      // very first render if this course has translated content for it —
      // otherwise the learner would see one flash of English before a swap.
      const initialLocale = getActiveLocale();
      await loadLocaleIndex(spec.id);
      if (!alive) return;
      const initialOverlay =
        initialLocale === "en" || !isLocaleAvailable(spec.id, initialLocale)
          ? null
          : await loadLocaleOverlay(spec.id, initialLocale);
      const localizedSpec = initialOverlay ? applyLocaleOverlay(spec, initialOverlay) : spec;
      if (!alive) return;
      setCourseOverlay(initialOverlay);
      setContentLocale(initialOverlay ? initialLocale : "en");
      useSim.getState().init(localizedSpec, c);
      // Wire the pre-rendered scripted-voice manifest for this course/locale so
      // audio.speak() plays static clips instead of hitting live OpenAI TTS per
      // learner (see lib/audio.ts). The "effective" locale is the language the
      // spoken text is actually in — the course's overlay language, or "en" when
      // it has none for the chosen UI locale.
      const effectiveVoiceLocale = initialOverlay ? initialLocale : "en";
      audio.setVoiceCourse(spec.id);
      audio.setVoiceLocale(effectiveVoiceLocale);
      void audio.loadVoiceManifest(spec.id);
      // Debug/authoring hook: expose the loaded (localized) spec so the build-time
      // voice-capture driver (ops/prerender-voice.mjs) can enumerate device beats.
      // Harmless in production, matches the existing __cfAudio/__cfStoryboard hooks.
      (window as unknown as Record<string, unknown>).__CF_SCENARIO__ = localizedSpec;
      // Build-time voice-capture navigator: jump straight to any beat in-place
      // (no reload) so the pre-render driver enumerates every device's lines
      // with ONE page load per course/locale. Mirrors the `?beat=` deep-link
      // logic below (inherits the last declared environmentId + waypoint) and
      // re-arms the screen power gate so screen-app devices can be powered on.
      // Only exposed in capture mode; harmless otherwise.
      if (captureMode) {
        (window as unknown as Record<string, unknown>).__cfGotoBeat = (beatId: string): boolean => {
          const target = localizedSpec.beats.find((x) => x.id === beatId);
          if (!target) return false;
          let envId: string | undefined;
          for (const bt of localizedSpec.beats) {
            if (bt.environmentId) envId = bt.environmentId;
            if (bt.id === beatId) break;
          }
          if (envId) setActiveEnvId(envId);
          let lastWaypoint: string | undefined;
          for (const bt of localizedSpec.beats) {
            if ("waypointId" in bt && bt.waypointId) lastWaypoint = bt.waypointId as string;
            if (bt.id === beatId) break;
          }
          const wp =
            ("waypointId" in target && (target as { waypointId?: string }).waypointId) ||
            lastWaypoint ||
            localizedSpec.scene.initialWaypointId;
          setCameraWaypointId(wp);
          setScreenOn(false);
          useSim.setState({ currentBeatId: beatId, phase: "running" });
          return true;
        };
        // QA in capture mode: read the store (current beat, outcomes, whatIf).
        (window as unknown as Record<string, unknown>).__cfState = () => useSim.getState();
      }
      setCameraWaypointId(spec.scene.initialWaypointId);
      // Dev-only: ?beat=<id> jumps straight to a beat (calibration / scene QA).
      // Sets the beat's environment + a matching waypoint so pano scenes frame up.
      try {
        const jump = new URLSearchParams(window.location.search).get("beat");
        if (jump) {
          const b = spec.beats.find((x) => x.id === jump);
          if (b) {
            // Walk backwards to inherit the last declared environmentId.
            let envId: string | undefined;
            for (const bt of spec.beats) {
              if (bt.environmentId) envId = bt.environmentId;
              if (bt.id === jump) break;
            }
            if (envId) setActiveEnvId(envId);
            // Walk backwards to inherit the last camera beat's waypoint too — this
            // mirrors what a natural playthrough would have set by the time this
            // beat is reached (guided/non-pano courses have no environmentId to
            // substring-match against, so without this the jump silently fell back
            // to the initial waypoint and framed the wrong part of the room).
            let lastWaypoint: string | undefined;
            for (const bt of spec.beats) {
              if ("waypointId" in bt && bt.waypointId) lastWaypoint = bt.waypointId as string;
              if (bt.id === jump) break;
            }
            const wp =
              ("waypointId" in b && b.waypointId) ||
              lastWaypoint ||
              spec.scene.waypoints.find((w) => w.id.includes(envId ?? "__none__"))?.id ||
              spec.scene.initialWaypointId;
            setCameraWaypointId(wp);
            useSim.setState({ currentBeatId: jump, phase: "running" });
            // A ?beat= link skips the Start button, and that click is what
            // normally starts the room bed. Without this the course is silent.
            const sceneEnv = envId
              ? spec.scene.environments?.find((e) => e.id === envId)
              : undefined;
            audio.resume();
            // A ?beat= visit never clicks Begin mission, which is what
            // normally lets the wall TVs start. Without this the news clip
            // stays on its first frame.
            openTvPlaybackGate();
            audio.startAmbient(
              sceneEnv?.ambientUrl ?? spec.ambientUrl,
              sceneEnv?.ambientVolume ?? spec.ambientVolume,
              sceneEnv?.music ?? spec.music ?? "office",
            );
          }
        }
      } catch { /* noop */ }
    });
    return () => {
      alive = false;
    };
  }, [showLibrary]);

  // Intro-card language selector (Change 8): re-localizes the CURRENT course
  // from the pristine base spec. Only meaningful while still on beat 0 (the
  // selector only renders on the intro card), so no progression state needs
  // to move — `setScenario` swaps just the content.
  async function handleLocaleChange(code: string) {
    if (code === uiLocale) return;
    const base = baseScenarioRef.current;
    setActiveLocale(code);
    const wantCourse = !!base && code !== "en" && isLocaleAvailable(base.id, code);
    const [overlay] = await Promise.all([
      base && wantCourse ? loadLocaleOverlay(base.id, code) : Promise.resolve(null),
      loadChromeOverlay(code),
    ]);
    // A later click wins.
    if (getActiveLocale() !== code) return;
    setUiLocale(code);
    setCourseOverlay(overlay);
    setContentLocale(overlay ? code : "en");
    // Keep the pre-rendered-voice lookup in sync with the content language.
    audio.setVoiceLocale(overlay ? code : "en");
    if (!base) return;
    useSim.getState().setScenario(overlay ? applyLocaleOverlay(base, overlay) : base);
  }

  if (showLibrary) return <IntroPage bodyPath="intro/bodies/library.html" fallback={<Library />} />;

  // When entering a camera beat, retarget the camera ONCE. If the beat targets a
  // new pose, start the tween and wait for onArrived to advance (do NOT advance
  // here — that bug made device beats activate ~80ms into the 2s zoom, so the
  // screen appeared while the camera was still flying in). If the beat targets the
  // pose we're already at (e.g. first camera beat == initial waypoint), advance
  // — unless `hold` / freeRoam / walkable (explore rooms must NOT end the course).
  useEffect(() => {
    if (beat?.type !== "camera" || !beat.waypointId) return;
    // Walkable rooms use FirstPersonControls, which TELEPORTS to a new spawn
    // the instant `cameraWaypointId` changes (Scene.tsx feeds the active
    // waypoint's position/target straight into its `spawn`/`lookTarget`
    // props) -- there is no tween to await, so CameraRig's `onArrived` never
    // fires here. ALWAYS apply the beat's waypoint (once per beat id) even
    // when `hold` is set -- `hold` only controls whether we auto-advance
    // afterward, never whether the camera actually moves. `b_walk` (the
    // free-roam beat) is `hold` but targets the SAME waypoint the previous
    // beat already set, so this is a no-op for it -- the learner keeps
    // driving the camera until an in-world hotspot calls `sim.advance()`.
    // A scripted `hold` cut to a DIFFERENT waypoint (e.g. the news-cut beat
    // snapping the view onto the TV) still needs to actually retarget the
    // camera; only the trailing auto-advance timer below is skipped for it
    // (a dedicated effect elsewhere drives its advance, or "Skip ahead").
    // A non-`hold` camera beat is a scripted "teleport to a new vantage
    // point" cut (walking up to the bar counter, or the hacker-room monitor
    // push-in): update the waypoint once, then advance after a short settle
    // delay so the beat pointer doesn't race ahead of the render (same
    // idempotent-timeout technique as the `freeExplore` branch below).
    if (scenario?.walkable) {
      if (handledCamBeat.current !== beat.id) {
        handledCamBeat.current = beat.id;
        if (beat.waypointId !== cameraWaypointId) {
          if (beat.walkSfx) audio.footsteps();
          setCameraWaypointId(beat.waypointId);
        }
      }
      if (beat.hold) return;
      const targetWp = scenario?.scene.waypoints.find((w) => w.id === beat.waypointId);
      const tweenMs = targetWp?.durationS && targetWp.durationS > 0 ? targetWp.durationS * 1000 : CAM_TWEEN_MS;
      const t = window.setTimeout(() => {
        if (useSim.getState().currentBeat()?.id === beat.id) useSim.getState().advance();
      }, tweenMs + 80);
      return () => window.clearTimeout(t);
    }
    // Free movement: the learner drives the camera (FirstPersonControls), so the
    // scripted CameraRig tween + its onArrived callback never run. Auto-advance
    // past cinematic camera beats (unless `hold`) so the guided decision flow
    // doesn't stall on a move the learner is expected to make themselves. The
    // timeout re-checks the live beat id, so it's idempotent across re-runs
    // (e.g. toggling free mode mid-beat).
    if (freeExplore) {
      if (beat.hold || scenario?.freeRoam) return;
      const t = window.setTimeout(() => {
        if (useSim.getState().currentBeat()?.id === beat.id) useSim.getState().advance();
      }, 150);
      return () => window.clearTimeout(t);
    }
    if (handledCamBeat.current === beat.id) return;
    handledCamBeat.current = beat.id;
    if (beat.waypointId !== cameraWaypointId) {
      if (beat.walkSfx) audio.footsteps();
      setCameraWaypointId(beat.waypointId); // tween runs; onArrived() advances
      return;
    }
    if (beat.hold || scenario?.freeRoam) return;
    const t = window.setTimeout(() => {
      if (useSim.getState().currentBeat()?.id === beat.id) useSim.getState().advance();
    }, 80);
    return () => window.clearTimeout(t);
  }, [beat?.id, beat?.type, beat?.waypointId, beat?.hold, cameraWaypointId, scenario?.freeRoam, freeExplore]);

  // No settings screen before a course: the intro card is the first screen and
  // stays readable while the 3D room loads behind it. Quality is detected
  // automatically (FpsWatchdog lowers it), reduced motion follows the OS
  // setting, and music + narrator have their own toggles in the course bar.
  // Browsers only allow audio after a user gesture, so the first click or key
  // on the page starts it.
  useEffect(() => {
    if (!scenario || !cap || !tier || sim.phase !== "ready") return;
    const qs = new URLSearchParams(window.location.search);
    if (qs.get("resume") === "1" || qs.get("beat")) return;
    const first = scenario.beats.find((b) => b.id === scenario.initialBeatId);
    if (first?.type !== "intro") return;
    sim.start();
    const sc = scenario;
    const unlock = () => {
      window.removeEventListener("pointerdown", unlock, true);
      window.removeEventListener("keydown", unlock, true);
      audio.setEnabled(soundOn);
      audio.resume();
      if (soundOn && !sc.walkable) audio.startAmbient(sc.ambientUrl, sc.ambientVolume, sc.music ?? "office");
    };
    window.addEventListener("pointerdown", unlock, true);
    window.addEventListener("keydown", unlock, true);
  }, [scenario, cap, tier, sim.phase]);

  // Look-only mode (privacyHuntLookOnly, computed below) needs its OWN camera
  // pose (`wp_privacy_lookonly`, well inside the room) instead of
  // `wp_privacy_entrance` (the FP walk spawn point, right at the threshold —
  // see that waypoint's comment). Retarget once per beat entry; OrbitControls
  // doesn't reposition the camera to a new target on its own (see Scene.tsx's
  // InspectSnap), so this just picks WHICH waypoint feeds that snap. NOTE:
  // must stay ABOVE the `if (!scenario || !cap || !tier) return` early-return
  // further down (React error #310 otherwise — a hook that only sometimes
  // runs, since privacyHuntLookOnly itself is computed after that gate).
  useEffect(() => {
    const lookOnly = !!beat?.forceFreeRoam && !!beat?.freeRoamGated && !sim.walkOptIn;
    if (beat?.id !== "b_privacy_hunt" || !lookOnly) return;
    if (lookOnlyCamSet.current === beat.id) return;
    lookOnlyCamSet.current = beat.id;
    setCameraWaypointId("wp_privacy_lookonly");
  }, [beat?.id, beat?.forceFreeRoam, beat?.freeRoamGated, sim.walkOptIn]);

  // Reset the screen to OFF whenever we return to the intro (fresh run / replay).
  useEffect(() => {
    if (beat?.type === "intro") {
      setScreenOn(false);
      // Reset the walkable storyboard so a replay starts from task 1.
      setTask1Done(false);
      setTask2Done(false);
      setTvOn(false);
      setTerminalOn(false);
      setDeskPcOpen(false);
      setDeskPcStage("login");
      // Only the opening intro clears the briefing hub. A later intro
      // (the office-privacy card inside another course) must not replay it.
      if (!scenario || beat.id === scenario.initialBeatId) setBreachBriefSeen(false);
    }
  }, [beat?.type, beat?.id, scenario]);

  // Dev/QA hooks for the walkable storyboard (harmless in prod, like ?beat=).
  // Lets headless QA flip task state + switch the TV on without a full walk.
  useEffect(() => {
    (window as unknown as { __cfStoryboard?: unknown }).__cfStoryboard = {
      completeTask1: () => setTask1Done(true),
      switchTvOn: () => {
        setTvOn(true);
        setTask2Done(true);
      },
      state: () => ({ task1Done, task2Done, tvOn }),
    };
  }, [task1Done, task2Done, tvOn]);

  // Multi-scene CUT: when a beat declares a new environment, swap the active 3D
  // room + its ambient bed. Courses with `roomWaypoints` (office-explore) fade
  // out → swap → fade in so room hops feel cinematic, not a hard flash.
  useEffect(() => {
    // The room you are in is the last environment declared up to this beat,
    // not only a beat that sets one itself. A deep link into a later beat
    // (the pool Wi-Fi lab) must not keep the office bed.
    let nextId = beat?.environmentId ?? null;
    if (!nextId && scenario && beat) {
      const idx = scenario.beats.findIndex((b) => b.id === beat.id);
      for (let i = idx; i >= 0; i--) {
        const id = scenario.beats[i].environmentId;
        if (id) {
          nextId = id;
          break;
        }
      }
    }
    if (!nextId || nextId === activeEnvId) return;
    const se = scenario?.scene.environments?.find((e) => e.id === nextId);
    const useFade = !!(scenario?.roomWaypoints && scenario.roomWaypoints.length > 0);

    const apply = () => {
      setActiveEnvId(nextId);
      if (se) audio.switchAmbient(se.ambientUrl, se.ambientVolume, se.music ?? "none");
      // Cutting into a room with no wall-mounted video screens of its own
      // (e.g. bar -> office) must leave ZERO audible residue from the room
      // we just left. Normal unmount already ref-counts videos down to zero,
      // but a hard, unconditional cache flush here removes any chance of a
      // stray video (e.g. a still-loading/queued play() from a rapid beat
      // sequence) surviving the cut — reported as "still hear the TV" when
      // the office scene should be TV-silent.
      if (!se?.environment.videoScreens || se.environment.videoScreens.length === 0) {
        stopAllVideoScreens();
      }
      // Opt-in jolt sting (Beat.alertSfx) for a hard cut into a threatening
      // room, e.g. the bar game's cut into the hacker's room right after the
      // SSL warning is dismissed — layered on top of the room's own ambient
      // bed (which for a "dark"-music room is already a continuous tense
      // drone; this is the one-shot punctuation on TOP of that).
      if (beat?.alertSfx) audio.breachAlert();
    };

    if (!useFade) {
      apply();
      return;
    }

    setRoomFade(true);
    let t2 = 0;
    const t1 = window.setTimeout(() => {
      apply();
      t2 = window.setTimeout(() => setRoomFade(false), 300);
    }, 340);
    return () => {
      window.clearTimeout(t1);
      if (t2) window.clearTimeout(t2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beat?.id]);

  // Walkable desk-PC: while the pointer is captured (WASD walk), you can't click
  // a DOM hotspot, so opening the sticky-note zoom works like an FPS "use" key —
  // look at the note (pin near screen centre) and click. When NOT captured the
  // DOM dot is directly clickable (rendered below). Esc closes the 2D zoom.
  const deskPcEnabled = !!scenario?.walkable || freeExplore;
  // Scene-1 ordering gate (office-under-attack): while the room has a reception
  // welcome terminal that hasn't been switched on yet, the desk PC / sticky
  // note cannot be opened — the learner must power the terminal on first.
  const noteLocked = !!scenario?.scene.environment.receptionTerminal && !terminalOn;

  // Bar-course only. That story cuts from the bar into a hacker room and the
  // bar bed must not leak across the cut. Quishing, ransomware and
  // spear-phishing use the same environment id "hacker" for their own dark
  // music. Muting every "hacker" id silenced those rooms, and the restore
  // key "bar" never matched, so the mute lasted for the rest of the course
  // (the living room included). Courses with no bar beat keep scene duck at 1.
  useEffect(() => {
    const isolatesBar =
      scenario?.beats.some((b) => b.environmentId === "bar" || b.environmentId === "bar_news") ?? false;
    if (!isolatesBar) {
      audio.duckScene(1);
      return;
    }
    if (beat?.environmentId === "hacker") audio.duckScene(0);
    else if (beat?.environmentId === "bar" || beat?.environmentId === "bar_news") audio.duckScene(0.3);
  }, [beat?.id, beat?.environmentId, scenario]);

  // Quiet the bed on a narrated card, quiz, or call so the line stays clear.
  // A device beat is not enough: the restaurant QR scan and the hacker-room
  // establish have no narration, and ducking them made the scene-start music
  // inaudible. Spoken lines still duck while they play, and a real <video>
  // ducks itself.
  useEffect(() => {
    const content =
      beat?.type === "quiz" ||
      beat?.type === "teach" ||
      beat?.type === "debrief" ||
      beat?.type === "voice_call" ||
      beat?.type === "video_call";
    if (content) audio.holdMediaDuck("activity");
    else audio.releaseMediaDuck("activity");
  }, [beat?.id, beat?.type]);


  useEffect(() => {
    if (!deskPcEnabled) return;
    const centered = (pin?: { xPct: number; yPct: number; visible: boolean }) =>
      !!pin?.visible && pin.xPct > 38 && pin.xPct < 62 && pin.yPct > 38 && pin.yPct < 62;
    const onClick = () => {
      if (deskPcOpen) return;
      const locked = (window as unknown as { __fpLocked?: boolean }).__fpLocked;
      if (!locked) return;
      const w = window as unknown as {
        __deskPin?: { xPct: number; yPct: number; visible: boolean; distance: number };
        __tvPin?: { xPct: number; yPct: number; visible: boolean; distance: number };
      };
      // Look-at "use" key: desk PC (any time) or, once task 1 is done, the TV.
      if (!noteLocked && centered(w.__deskPin) && (w.__deskPin?.distance ?? 99) < 4) {
        if (document.pointerLockElement) document.exitPointerLock();
        setDeskPcOpen(true);
        return;
      }
      if (task1Done && !task2Done && centered(w.__tvPin) && (w.__tvPin?.distance ?? 99) < 5.5) {
        setTvOn(true);
        setTask2Done(true);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && deskPcOpen) setDeskPcOpen(false);
    };
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [deskPcEnabled, deskPcOpen, task1Done, task2Done, noteLocked]);

  // A "title" beat is a cinematic caption ("A few days later…") — auto-advance.
  useEffect(() => {
    if (beat?.type !== "title") return;
    const ms = beat.autoAdvanceMs ?? 3200;
    const t = window.setTimeout(() => {
      if (useSim.getState().currentBeat()?.id === beat.id) useSim.getState().advance();
    }, ms);
    return () => window.clearTimeout(t);
  }, [beat?.id, beat?.type, beat?.autoAdvanceMs]);

  // News-cut beats (item h, 2026-07-29): on a walkable course a `camera` beat
  // without `hold` auto-advances the instant its (short) tween finishes —
  // which fired the teach card ~9s in, WHILE the looping breaking-news video
  // was still playing (the video's own `loop=true` means it has no real
  // `ended` event to gate on). Authoring `hold:true` on the newsCut beat
  // above suppresses that premature timer entirely; this effect replaces it
  // with a duration matched to the anchor clip's spoken length so the beat
  // still moves on by itself once the segment has played, while "Skip ahead"
  // (rendered unconditionally for any `newsCut` beat) always lets the
  // learner move on sooner.
  useEffect(() => {
    if (beat?.type !== "camera" || !beat.newsCut) return;
    const ms = beat.autoAdvanceMs ?? 16000;
    const t = window.setTimeout(() => {
      if (useSim.getState().currentBeat()?.id === beat.id) useSim.getState().advance();
    }, ms);
    return () => window.clearTimeout(t);
  }, [beat?.id, beat?.type, beat?.newsCut, beat?.autoAdvanceMs]);

  // Pack beat: force 3D pano look-around (OrbitControls). Must run before any
  // conditional return so hook order stays stable once the scenario has loaded.
  const deviceEarly =
    beat?.type === "device" && scenario
      ? scenario.devices.find((x) => x.id === beat.deviceId) ?? null
      : null;
  const packCfgEarly = deviceEarly?.kind === "packlist" ? (deviceEarly.config as PackListConfig) : null;
  const chargeCfgEarly = deviceEarly?.kind === "cafecharge" ? (deviceEarly.config as CafeChargeConfig) : null;
  const lostCfgEarly = deviceEarly?.kind === "lostdevice" ? (deviceEarly.config as LostDeviceConfig) : null;
  const isPackPanoEarly = !!(
    packCfgEarly?.plateUrl &&
    (packCfgEarly.plateHotspots?.length ?? 0) > 0
  );
  const isChargePanoEarly = !!(
    chargeCfgEarly?.plateUrl &&
    (chargeCfgEarly.hotspots?.length ?? 0) > 0
  );
  const isLostPanoEarly = !!lostCfgEarly?.bagHotspot;
  // Any pano beat that projects clickable pins onto the Marble world.
  const isPinPanoEarly = isPackPanoEarly || isChargePanoEarly || isLostPanoEarly;
  // The pins to project this beat (pack items or charge hotspots).
  const pinItemsEarly: PackItemSpec[] | null = isPackPanoEarly
    ? packCfgEarly!.plateHotspots ?? null
    : isChargePanoEarly
    ? ((chargeCfgEarly!.hotspots as unknown) as PackItemSpec[])
    : isLostPanoEarly
    ? [lostCfgEarly!.bagHotspot!]
    : null;
  useEffect(() => {
    setInspectMode(isPinPanoEarly);
    if (!isPinPanoEarly) setPackPinScreens([]);
  }, [beat?.id, isPinPanoEarly]);

  useEffect(() => {
    setPcStage(null);
    setTabletStage(null);
    setCafeStage(null);
    setLaptopStage(null);
    setBarLaptopStage(null);
    setHolidayStage(null);
    setMitmLineIdx(0);
    setAwareStage(null);
  }, [beat?.id]);

  // `screenOn` is a single shared "is a screen currently lit" flag reused by
  // every desk-monitor beat across episodes 4-9 (sign in once, stays on for
  // desktop/mail/browser/aiassistant/inboxtriage). Episode 10 introduces a
  // SECOND, physically different screen (the personal laptop) — force a
  // fresh power-on gate for it rather than inheriting "on" from the desk.
  useEffect(() => {
    if (
      beat?.id === "b_cam_personallaptop" ||
      beat?.id === "b_wifi_pool_wide" ||
      beat?.id === "b_ipad" ||
      beat?.id === "b_recon" ||
      beat?.id === "b_home" ||
      beat?.environmentId === "attacker"
    ) {
      if (beat?.id === "b_home") {
        setScreenOn(true);
        return;
      }
      setScreenOn(false);
    }
  }, [beat?.id, beat?.environmentId]);
  // Beat.screen: "off" = a different physical screen (fresh power-on gate),
  // "on" = the device starts lit. Only beats that set it are affected.
  useEffect(() => {
    if (beat?.screen === "off") setScreenOn(false);
    else if (beat?.screen === "on") setScreenOn(true);
  }, [beat?.id, beat?.screen]);

  // Global UI "mouse click" sound on any clickable element (buttons, links,
  // desktop icons, mail rows, decision buttons...). Component-agnostic: anything
  // with cursor:pointer triggers it. The power button plays its own chime instead.
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      let el = e.target as HTMLElement | null;
      for (let i = 0; i < 4 && el; i++, el = el.parentElement) {
        if (el.classList?.contains("screen-power")) return;
        if (getComputedStyle(el).cursor === "pointer") {
          audio.click();
          return;
        }
      }
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, []);

  // Branded loader for glTF rooms: GltfRoom / StaticGltfProp fire `cf-gltf-ready`
  // with `{ detail: { url } }`. Readiness is keyed to the *current* environment
  // so a late pool event cannot unlock the beach, and we wait for main mesh +
  // static props (crowd) before the branded splash lifts.
  const envForGltf = scenario ? resolveEnvironment(scenario, activeEnvId) : null;
  const requiredGltfUrls = envForGltf?.gltfUrl
    ? [envForGltf.gltfUrl, ...(envForGltf.staticGltfProps?.map((p) => p.url) ?? [])]
    : [];

  useLayoutEffect(() => {
    const onReady = (ev: Event) => {
      const url = (ev as CustomEvent<{ url?: string }>).detail?.url;
      if (!url) return;
      setLoadedGltfUrls((prev) => {
        if (prev.has(url)) return prev;
        const next = new Set(prev);
        next.add(url);
        return next;
      });
    };
    window.addEventListener("cf-gltf-ready", onReady);
    return () => window.removeEventListener("cf-gltf-ready", onReady);
  }, []);

  useEffect(() => {
    if (!scenario || beat?.id !== "b_bar_title") return;
    const beach = scenario.scene.environments?.find((e) => e.id === "beach")?.environment;
    if (!beach?.gltfUrl) return;
    preloadGltfUrls([beach.gltfUrl, ...(beach.staticGltfProps?.map((p) => p.url) ?? [])]);
  }, [scenario, beat?.id]);
  // Beat.preloadEnvironmentId: warm the next room's glTF during this beat.
  useEffect(() => {
    const id = beat?.preloadEnvironmentId;
    if (!scenario || !id) return;
    const env = scenario.scene.environments?.find((e) => e.id === id)?.environment;
    if (!env?.gltfUrl) return;
    preloadGltfUrls([env.gltfUrl, ...(env.staticGltfProps?.map((p) => p.url) ?? [])]);
  }, [scenario, beat?.id, beat?.preloadEnvironmentId]);
  // Beat.fadeCut: the overlay must outlive a camera beat that advances at once.
  useEffect(() => {
    if (!beat?.fadeCut) return;
    const id = beat.id;
    setFadeCutKey(id);
    window.setTimeout(() => setFadeCutKey((k) => (k === id ? null : k)), 1700);
  }, [beat?.id, beat?.fadeCut]);

  const gltfReady =
    !envForGltf?.gltfUrl || requiredGltfUrls.every((u) => loadedGltfUrls.has(u));

  // Re-render the intro card as the room's bytes arrive (pano/procedural rooms
  // report through this external store; glTF rooms also flip `gltfReady` React
  // state). This drives both the intro load bar and the Begin button's gate.
  useSyncExternalStore(subscribeLoadProgress, getLoadVersion);

  if (!scenario || !cap || !tier) {
    return (
      <div className="sim-root">
        <div className="splash">
          <BrandMark className="splash-brand" onlyWhenBranded />
          <h1>{tx("Interactive 3D Training")}</h1>
          <p>{t("intro.preparing")}</p>
        </div>
      </div>
    );
  }

  const activeEnv = resolveEnvironment(scenario, activeEnvId);

  const device = beat?.deviceId
    ? scenario.devices.find((d) => d.id === beat.deviceId) ?? null
    : null;

  // Episode-12 finale branch, resolved once so both the Terminal-chrome
  // render below and any future guide overrides read the same values.
  const endingBranchKey = beat && beat.type === "ending" ? sim.endingBranch() : null;
  const endingCfg =
    beat && beat.type === "ending" && endingBranchKey ? beat.ending?.branches[endingBranchKey] ?? null : null;

  const packCfg = packCfgEarly;
  const chargeCfg = chargeCfgEarly;
  const isPackPano = isPackPanoEarly;
  const isChargePano = isChargePanoEarly;
  const isPinPano = isPinPanoEarly;
  const packReviewConfig = scenario.devices.find((item) => item.kind === "packlist")
    ?.config as PackListConfig | undefined;
  const packReviewItems =
    beat?.id === "b_teach_pack" && packFeedback
      ? (packReviewConfig?.plateHotspots ?? [])
          .filter((item) => item.role !== "info")
          .map((item) => {
            const selected = packFeedback.packedIds.includes(item.id);
            const shouldPack = item.role === "safe";
            const correct = selected === shouldPack;
            return {
              id: item.id,
              label: item.label,
              choice: selected ? ("Packed" as const) : ("Left behind" as const),
              verdict: correct
                ? shouldPack
                  ? "Approved"
                  : "Correct"
                : shouldPack
                ? "Missing"
                : "Risky",
              tone: correct ? ("good" as const) : shouldPack ? ("warn" as const) : ("risk" as const),
              explanation: item.detail ?? "",
            };
          })
      : undefined;

  // Only monitor/laptop-screen apps use the power-on gate; room overlays don't.
  const isScreenApp = beat?.type === "device" && !!device && SCREEN_KINDS.has(device.kind);
  // pool-walkable iPad: glue the power button + lock chrome to the projected
  // tablet quad (upward-facing). Never fall back to the viewport-centered
  // --scr-* box — that painted over her head on the failed zoom.
  const mfitIpad = device?.kind === "mfitchat" && (device.config as MfitChatConfig).frame === "ipad";
  const mfitGuideKind = !!device?.kind && (device.kind.startsWith("mfit") || device.kind === "mfapc" || device.kind === "mfaphone" || device.kind === "clickfix" || device.kind === "oneclick" || device.kind === "invoice" || device.kind === "devday");
  const mfitRecord = (list: { eventId: string; evidence?: string }[]) => {
    for (const it of list) {
      if (!beat) break;
      const positive = Object.values(scenario.voiceAttackers).some((va) =>
        va.scoringEvents.some((e) => e.id === it.eventId && e.kind === "positive"),
      );
      sim.recordOutcome({ type: positive ? "refusal" : "disclosure", beatId: beat.id, eventId: it.eventId, evidence: it.evidence });
    }
  };
  const mfitDone = (d: { key?: string; eventId?: string; evidence?: string; events?: { eventId: string; evidence?: string }[] }) => {
    const list = [...(d.eventId ? [{ eventId: d.eventId, evidence: d.evidence }] : []), ...(d.events ?? [])];
    for (const it of list) {
      if (!beat) break;
      const positive = Object.values(scenario.voiceAttackers).some((va) =>
        va.scoringEvents.some((e) => e.id === it.eventId && e.kind === "positive"),
      );
      sim.recordOutcome({ type: positive ? "refusal" : "disclosure", beatId: beat.id, eventId: it.eventId, evidence: it.evidence });
    }
    sim.advance(d.key);
  };
  const ipadGlued =
    (device?.kind === "pooltablet" || device?.kind === "holidaytablet" || device?.kind === "holidaymeeting" || device?.kind === "holidayrecon" || mfitIpad) &&
    !!screenBox &&
    screenBox.widthPct > 8 &&
    (screenBox.visible || screenOn);
  const huntEnlarge = device?.kind === "holidaytablet" && !!holidayStage?.startsWith("hunt");
  const ipadGlueStyle = ipadGlued
    ? glueOverlayStyle(
        screenBox!,
        device?.kind === "holidayrecon" ? 0.015 : huntEnlarge ? -0.055 : 0,
      )
    : captureMode
      ? {
          position: "absolute" as const,
          left: "18%",
          top: "8%",
          width: "64%",
          height: "82%",
          zIndex: 42,
        }
      : undefined;
  const glueScreenKind =
    device?.kind === "pooltablet" ||
    device?.kind === "holidaytablet" ||
    device?.kind === "holidaymeeting" ||
    device?.kind === "holidayrecon";
  const showPowerGate = isScreenApp && !screenOn && (!glueScreenKind || ipadGlued || captureMode);
  const powerLabel =
    device?.kind === "desklogin" || device?.kind === "barlaptop"
      ? "Click to sign in"
      : device?.kind === "pooltablet" ||
          device?.kind === "holidaytablet" ||
          device?.kind === "holidaymeeting"
        ? "Turn on the iPad"
        : "Turn on the screen";
  // Is the top-right storyboard/guide card visible for this beat? (Mirrors the
  // render condition below.) When it is, the voice transcript lives INSIDE that
  // card (guide-voice section), so the standalone top-center VoicePanel is
  // suppressed to avoid showing the caption twice.
  const isHolidayIntroCard =
    scenario.id === "holiday-call" && !!beat && HOLIDAY_INTRO_NARRATION_BEATS.has(beat.id);
  const holidayIntroLive = isHolidayIntroCard && beat?.type !== "intro";
  const storyCardShown =
    !!beat &&
    beat.type !== "intro" &&
    beat.type !== "title" &&
    !isHolidayIntroCard &&
    !(
      beat.type === "device" &&
      device?.kind === "packlist" &&
      (device.config as PackListConfig).plateUrl
    ) &&
    !!beat.guide;
  const platformCinemaPhase =
    activeEnvId === "platform_kiosk" && beat
      ? beat.id === "b_plat_establish"
        ? "establish"
        : beat.id === "b_plat_kiosk"
        ? "approach"
        : beat.id === "b_plat_screen" || beat.id === "b_wifi"
        ? "screen"
        : null
      : null;
  const bizCinemaPhase =
    activeEnvId === "business_center" && beat
      ? beat.id === "b_biz_establish" || beat.id === "b_film_biz_establish"
        ? "establish"
        : beat.id === "b_biz_approach" || beat.id === "b_film_biz_approach"
        ? "approach"
        : beat.id === "b_biz_screen" || beat.id === "b_biz_pc" || beat.id === "b_film_biz_screen" || beat.id === "b_film_pc"
        ? "screen"
        : null
      : null;
  // remote-work: hardcoded lobby/platform/carriage device beats.
  // office-explore (and future courses): env.lookAround + beat.hold = Marble
  // explore room (OrbitControls on, Continue to leave) without covering the
  // pano with a full teach card first.
  const isAlwaysLookPano =
    !!(activeEnv.lookAround && beat?.hold) ||
    (beat?.type === "device" &&
      (activeEnvId === "lobby" || activeEnvId === "platform" || activeEnvId === "carriage"));

  // Which app the learner should open this beat (drives taskbar highlight + toast).
  const targetApp =
    beat?.type === "device" && beat.completeOn?.action === "open_app"
      ? beat.completeOn.arg
      : undefined;
  // Lure email -> desktop "new message" toast when we're steering them to Mail.
  const mailDevice = scenario.devices.find((d) => d.kind === "mail");
  const mailEmails = (mailDevice?.config as MailConfig | undefined)?.emails ?? [];
  const lureEmail = mailEmails.find((e) => e.lure) ?? mailEmails[0];
  const desktopToast =
    targetApp === "mail" && lureEmail
      ? { from: lureEmail.from, subject: lureEmail.subject, appId: "mail" }
      : null;

  // Shows every playthrough by design (see LookAroundTutorial.tsx) --
  // `tutorialDismissed` (component state, reset on reload) still stops it
  // repeating on every qualifying beat within one run.
  // Office Floor Walk (roomWaypoints): pure camera tour — no tutorial overlay.
  const isRoomWaypointTour = !!(scenario?.roomWaypoints && scenario.roomWaypoints.length > 0);
  const isFreeRoamPoc = !!scenario?.freeRoam;
  const isWalkablePoc = !!scenario?.walkable;
  // A single beat that forces real WASD/drag-to-look free-roam regardless of
  // the learner's saved movement preference or `scenario.allowFreeExplore`
  // (see types.ts Beat.forceFreeRoam) — e.g. Episode 3's "find the risks"
  // room search on an otherwise fully-guided course.
  // `freeRoamGated` beats (see types.ts) only grant real WASD walking once the
  // learner opted in via the checkbox on an earlier `offerFreeRoamCheckbox`
  // intro beat — otherwise they stay look-only (fixed-eye 360deg rotate).
  const forceFreeRoamActive = !!beat?.forceFreeRoam && (!beat?.freeRoamGated || sim.walkOptIn);
  // The look-only counterpart: a gated forceFreeRoam beat the learner did NOT
  // opt into still needs SOME camera control (OrbitControls), or the camera
  // would be frozen with no way to look around at all.
  const privacyHuntLookOnly = !!beat?.forceFreeRoam && !!beat?.freeRoamGated && !sim.walkOptIn;
  // A walkable room backed by a real glTF model needs the branded loader until
  // the geometry/textures are on the GPU (see `cf-gltf-ready`).
  const needsGltfLoader = !!activeEnv.gltfUrl;
  const showGltfLoader =
    needsGltfLoader && !gltfReady && sim.phase === "running" && !captureMode && beat?.type !== "intro";
  // The room always mounts behind the intro card (Scene renders whenever not in
  // capture mode), so its download/parse finishes while the learner reads the
  // briefing. The Begin button stays disabled until it is usable — mirrors the
  // IntroLoadBar's readiness so the two never disagree. Capture mode has no
  // Scene, so it is treated as ready (QA/screenshots must not deadlock).
  const introLoaderKeys = needsGltfLoader
    ? requiredGltfUrls
    : activeEnv.enablePano && activeEnv.panoUrl
      ? [activeEnv.panoUrl]
      : [];
  const introSceneLoaded =
    captureMode ||
    (introLoaderKeys.length === 0
      ? isSceneReady()
      : needsGltfLoader
        ? gltfReady
        : summarizeLoad(introLoaderKeys).allDone);
  // Real bytes for the start reader's progress + time estimate.
  const introSum = summarizeLoad(introLoaderKeys);
  // A guided glTF-room course that also allows free walking (office-under-attack)
  // gets the same start-of-course navigation tutorial the pano courses get.
  const isGltfRoomCourse =
    !!activeEnv.gltfUrl && (!!scenario?.allowFreeExplore || forceFreeRoamActive || privacyHuntLookOnly);
  const needsLookAroundTutorial =
    sim.phase === "running" &&
    // Show the nav tutorial only AFTER the intro briefing card is dismissed, so
    // the two full-screen overlays never stack on top of each other.
    beat?.type !== "intro" &&
    !tutorialDismissed &&
    !isRoomWaypointTour &&
    !isFreeRoamPoc &&
    !isWalkablePoc &&
    (isPinPanoEarly || isAlwaysLookPano || isGltfRoomCourse || !!beat?.lookInPlace);
  const tutorialVariant: "look" | "walk" = freeExplore || forceFreeRoamActive ? "walk" : "look";

  return (
    <div className="sim-root" data-env={activeEnv.procedural ?? ""} data-exposure-map={scenario?.exposureMap ? "1" : undefined}>
      {showGltfLoader && (
        <div className="cf-room-loader" role="status" aria-live="polite">
          <BrandMark className="cf-room-loader__mark" />
          <div className="cf-room-loader__ring" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <div className="cf-room-loader__title">{gltfLoaderTitle(scenario.id, activeEnv.procedural)}</div>
          <div className="cf-room-loader__sub">
            {tx("Loading the 3D environment. This takes a few seconds the first time.")}
          </div>
        </div>
      )}
      {!captureMode && (needsLookAroundTutorial || showTutorial) && (
        <LookAroundTutorial
          variant={isGltfRoomCourse ? tutorialVariant : "look"}
          canSwitchMode={!isWalkablePoc && !!scenario?.allowFreeExplore}
          onDone={() => {
            setTutorialDismissed(true);
            setShowTutorial(false);
          }}
        />
      )}
      {!captureMode && (
      <Scene
        scenario={scenario}
        environment={activeEnv}
        paused={
          (device?.kind === "holidaybriefing" && holidayStage === "watch") ||
          (device?.kind === "holidaymeeting" && holidayStage !== null && holidayStage !== "invite")
        }
        tier={tier}
        reducedMotion={cap.reducedMotion}
        waypointId={cameraWaypointId}
        inspectMode={
          // Walkable PoC owns the camera (WASD) — never force OrbitControls.
          isWalkablePoc ? false : inspectMode || isPinPano || isAlwaysLookPano || privacyHuntLookOnly || !!beat?.lookInPlace
        }
        // 2026-07-27 fix: the privacy hunt's look-only camera used the generic
        // "inspect a nearby object" orbit (target = a point across the room,
        // 0.8-3.5m orbit radius) which let dragging swing the camera all the
        // way around that point — clipping straight through the room's own
        // walls to see the exterior/backside. `spinInPlace` reuses the SAME
        // "eye == target, spin without moving" math the pano viewer uses
        // (see PanoOrbitBootstrap in Scene.tsx) so the camera position stays
        // pinned near `wp_privacy_lookonly` and only the look DIRECTION
        // changes, even though this is a procedural room, not a pano photo.
        // `spinYawLimitDeg` additionally caps HOW FAR it can spin — a real
        // glTF room still has doorways/windows only a couple of metres away.
        spinInPlace={privacyHuntLookOnly || !!beat?.lookInPlace}
        spinYawLimitDeg={privacyHuntLookOnly ? 78 : beat?.lookInPlace ? beat.lookInPlace.yawLimitDeg ?? 70 : undefined}
        packItems={isPinPano ? pinItemsEarly ?? undefined : undefined}
        onPackPins={isPinPano ? setPackPinScreens : undefined}
        onDeskPin={activeEnv.deskPc ? setDeskPin : undefined}
        onLoungePin={activeEnv.privacyRoom?.furniture?.some((f) => f.kind === "laptop" && f.stickyCaption) ? setLoungePin : undefined}
        onTvPin={activeEnv.conferenceTv ? setTvPin : undefined}
        onTerminalPin={activeEnv.receptionTerminal ? setTerminalPin : undefined}
        onIntercomPin={activeEnv.entryIntercom ? setIntercomPin : undefined}
        phoneRinging={
          !!activeEnv.deskPhone &&
          (beat?.id === "b_vish_ring" || (beat?.type === "device" && device?.kind === "livevishing"))
        }
        onPackagePin={activeEnv.deskPc && activeEnv.deskPackage ? setPackagePin : undefined}
        onCounterPin={activeEnv.counterHotspot ? setCounterPin : undefined}
        onScreenBox={activeEnv.screenQuad ? setScreenBox : undefined}
        beatScreenQuad={beat?.screenQuad}
        arcadeSpots={activeEnv.arcadeSpots}
        arcadeTv={activeEnv.arcadeTv}
        arcadeHub={!!beat?.arcadeHub}
        onArcadePins={activeEnv.arcadeSpots ? setArcadePins : undefined}
        privacyItems={activeEnv.privacyRoom?.items}
        privacyFurniture={activeEnv.privacyRoom?.furniture}
        privacyFoundIds={privacyFoundIds}
        onPrivacyPins={activeEnv.privacyRoom ? setPrivacyPins : undefined}
        tvOn={tvOn}
        freeExplore={!isWalkablePoc && (forceFreeRoamActive || (!!scenario?.allowFreeExplore && freeExplore))}
        onDemote={() => setTier((t) => (t ? nextLowerTier(t) : t))}
        onArrived={() => {
          // Camera beats auto-advance once the move finishes — unless `hold`
          // (Marble explore rooms wait for an explicit Continue).
          const b = useSim.getState().currentBeat();
          if (b?.type === "camera" && !b.hold) {
            useSim.getState().advance();
          }
        }}
      />
      )}

      {sim.phase === "running" && platformCinemaPhase && (
        <CinemaScreen
          imageUrl="/cdn/sim/remote-work/env-refs/scene_platform_front.png"
          aspect={1536 / 1024}
          originPct={{ x: 49.5, y: 41.8 }}
          scale={
            // 2026-07-14: bumped 3.0 -> 3.3. 2026-07-15: bumped 3.3 -> 3.6
            // ("terminal reads as shrunk / names cut off" feedback).
            // screenBox's centre (49.45%,41.8%) sits almost exactly on
            // originPct (49.5%,41.8%), so a bigger scale just makes the
            // kiosk occupy more of the letterboxed frame without clipping --
            // at 3.6x the screenBox's own height is ~23%*3.6=82.8% of the
            // frame's rendered box (still a safe margin below the 100% edge
            // where it would start clipping). NOTE this alone does not fix
            // ellipsis-truncated SSID names -- that's a relative layout
            // issue fixed by stacking the network row onto two lines
            // (.wk-net-info) so the name gets ~4x its old width budget
            // regardless of absolute size; this scale bump just makes the
            // whole terminal read as bigger/less "shrunk" on top of that.
            platformCinemaPhase === "establish" ? 1 : platformCinemaPhase === "approach" ? 1.3 : 3.6
          }
          screenBox={{ left: 39.0, top: 30.3, width: 20.9, height: 23.0 }}
        >
          {platformCinemaPhase === "screen" &&
            beat?.type === "device" &&
            device?.kind === "wifikiosk" && (
              <WifiKiosk
                config={device.config as WifiKioskConfig}
                onChoose={(n) => {
                  if (n.eventId) {
                    sim.recordOutcome({
                      type: n.trusted ? "refusal" : "disclosure",
                      beatId: beat.id,
                      eventId: n.eventId,
                      evidence: n.ssid,
                    });
                  }
                  sim.advance(n.outcome ?? (n.trusted ? "safe" : "risky"));
                }}
              />
            )}
        </CinemaScreen>
      )}

      {sim.phase === "running" && bizCinemaPhase && (
        // screenBox/originPct RE-MEASURED 2026-07-13 (second pass) via a
        // per-pixel luminance scan of the actual dark monitor rectangle in
        // scene_business_center.png (1536x1024), cross-checked at multiple
        // rows/columns to rule out perspective skew: the real bbox is px
        // (494,352)-(1008,647) -> left 32.2% top 34.4% width 33.4%
        // height 28.8%, centre (48.9%, 48.8%). The PRIOR "re-measured"
        // values (`width:36.5`, `height:22.2`, centre 50.5/45.5) were
        // themselves wrong: width overshot the true right edge by ~3pp
        // (the DOM overlay ran past the monitor bezel onto the wall/
        // cabinet) AND height undershot the true bottom edge by ~6.6pp
        // (the overlay stopped well above the screen's actual bottom) --
        // exactly the reported "goes over on the right, height too little"
        // symptom. Width is still the binding constraint on zoom (max safe
        // scale ~= 95/33.4≈2.84); scale=2.3 stays well inside that budget.
        <CinemaScreen
          imageUrl="/cdn/sim/remote-work/env-refs/scene_business_center.png"
          aspect={1536 / 1024}
          originPct={{ x: 48.9, y: 48.8 }}
          scale={bizCinemaPhase === "establish" ? 1 : bizCinemaPhase === "approach" ? 1.3 : 2.3}
          screenBox={{ left: 32.2, top: 34.4, width: 33.4, height: 28.8 }}
        >
          {bizCinemaPhase === "screen" && beat?.type === "device" && device?.kind === "publicpc" && (device.config as PublicPcConfig).film && (
            <PublicPcFilm
              key={beat.id}
              config={device.config as PublicPcConfig}
              onStageChange={setPcStage}
              onDone={() => sim.advance()}
            />
          )}
          {bizCinemaPhase === "screen" && beat?.type === "device" && device?.kind === "publicpc" && !(device.config as PublicPcConfig).film && (
            <PublicPc
              config={device.config as PublicPcConfig}
              onStageChange={setPcStage}
              onComplete={(result) => {
                sim.recordOutcome({
                  type: result.safe ? "refusal" : "disclosure",
                  beatId: beat.id,
                  eventId: result.eventId,
                  evidence: result.label,
                });
                sim.advance(result.outcome);
              }}
            />
          )}
        </CinemaScreen>
      )}

      {sim.phase === "running" &&
        !isRoomWaypointTour &&
        !isFreeRoamPoc &&
        !isWalkablePoc &&
        scenario?.riskMeter !== false && <RiskMeter />}

      {sim.phase === "running" &&
        !isRoomWaypointTour &&
        !isFreeRoamPoc &&
        !isWalkablePoc &&
        scenario?.exposureMap && device?.kind !== "privacyhunt" && <ExposureMap />}

      {/* Course-chapters read-only review (Change 10): a small, unmissable
          banner for as long as `reviewFromBeatId` is set, so it's always
          obvious the learner is replaying a past chapter and nothing they do
          here counts — with a one-click way back to their real position. */}
      {sim.reviewFromBeatId && (
        <div className="cf-review-banner" role="status">
          <span className="cf-review-banner__dot" aria-hidden />
          {tx("Reviewing a completed chapter — nothing here affects your score.")}
          <button type="button" className="cf-review-banner__exit" onClick={() => sim.exitReview()}>
            {tx("Exit review")}
          </button>
        </div>
      )}

      {sim.phase === "running" &&
        !storyCardShown &&
        !isHolidayIntroCard &&
        !VOICE_PANEL_SUPPRESS_KINDS.has(device?.kind ?? "") &&
        beat?.type !== "video_call" &&
        beat?.type !== "voice_call" &&
        // Consequence beats already print the spoken line in `.cq-lines`.
        // Keeping VoicePanel on cctv:false beats duplicated the line and
        // clipped it to "AI-generat.." over the holiday fake-meeting reveal.
        beat?.type !== "consequence" &&
        // Same cross-stacking-context reasoning as the GuideTooltip suppress
        // above: `.voice-panel` is `position:fixed` but outside `.device-layer`
        // with z-index 52, so it would float over the AI briefing modal (which
        // is trapped inside `.device-layer`'s stacking context) regardless of
        // the modal's own z-index.
        !(device?.kind === "aiassistant" && aiStage === "briefing") && <VoicePanel />}

      {sim.phase === "running" &&
        (isWalkablePoc || freeExplore || forceFreeRoamActive) &&
        !showGltfLoader &&
        beat?.type !== "intro" &&
        beat?.type !== "device" &&
        !(beat?.type === "camera" && !beat.hold) &&
        !deskPcOpen &&
        (() => {
          // Context-aware interact: near the desk → open the PC; once task 1 is
          // done and near the TV → switch it on. Storyboard gates task 2 on 1.
          const deskNear = !!activeEnv.deskPc && deskPin.visible && deskPin.distance < 4;
          const tvNear =
            !!activeEnv.conferenceTv && tvPin.visible && tvPin.distance < 5.5;
          let interactLabel = "Nothing to interact with nearby";
          let interactEnabled = false;
          let onInteract = () => {};
          if (deskNear && noteLocked) {
            interactLabel = "Switch on the welcome terminal first";
          } else if (deskNear) {
            interactLabel = task1Done ? "Use the PC" : "Sign in to the PC";
            interactEnabled = true;
            onInteract = () => {
              if (document.pointerLockElement) document.exitPointerLock();
              setDeskPcOpen(true);
            };
          } else if (tvNear && task2Done) {
            interactLabel = "TV is on";
          } else if (tvNear && task1Done) {
            interactLabel = "Switch on the TV";
            interactEnabled = true;
            onInteract = () => {
              setTvOn(true);
              setTask2Done(true);
            };
          } else if (tvNear && !task1Done) {
            interactLabel = "Finish task 1 first";
          }
          return (
            <WalkControls
              interactLabel={interactLabel}
              interactEnabled={interactEnabled}
              onInteract={onInteract}
              showExtras={!scenario?.walkHudMinimal}
            />
          );
        })()}

      {/* Walkable desk-PC sticky-note hotspot. When the pointer is NOT captured
          the dot is directly clickable; when captured, a centred reticle prompt
          tells the learner to click to read the note (handled in the effect).
          Hidden entirely while the reception terminal is still off (noteLocked)
          — the learner must switch the welcome terminal on first. */}
      {sim.phase === "running" &&
        activeEnv.deskPc &&
        // Guided (non-walkable) courses like office-under-attack place their
        // OWN sticky-note prop on this same desk (the Episode-2 deskPackage)
        // — this generic "Read note" -> full PC login/browser flow is a
        // walkable-mode-only interaction (see the Task-1/Task-2 notes below)
        // and must stay gated off whenever the environment has its own
        // deskPackage flow, or it bleeds through as a second, unrelated
        // clickable "Read note" pill next to the package (even if the
        // learner has free-explore mode on globally elsewhere).
        !activeEnv.deskPackage &&
        deskPcEnabled &&
        !showGltfLoader &&
        !deskPcOpen &&
        !noteLocked &&
        deskPin.visible && (
          <>
            <button
              type="button"
              className="cf-desk-hotspot"
              style={{ left: `${deskPin.xPct}%`, top: `${deskPin.yPct}%` }}
              title={tx("Read the sticky note")}
              onClick={(e) => {
                e.stopPropagation();
                if (document.pointerLockElement) document.exitPointerLock();
                setDeskPcOpen(true);
              }}
            >
              <span className="cf-desk-hotspot__dot" />
              <span className="cf-desk-hotspot__label">{t("desk.readNote")}</span>
            </button>
            {deskPin.xPct > 40 &&
              deskPin.xPct < 60 &&
              deskPin.yPct > 40 &&
              deskPin.yPct < 60 && (
                <div className="cf-desk-reticle" aria-hidden="true">
                  <span className="cf-desk-reticle__ring" />
                  <span className="cf-desk-reticle__hint">{t("desk.clickToReadNote")}</span>
                </div>
              )}
          </>
        )}

      {/* Conference-TV hotspot (task 2): directly clickable when not captured. */}
      {sim.phase === "running" &&
        activeEnv.conferenceTv &&
        !showGltfLoader &&
        !deskPcOpen &&
        task1Done &&
        !task2Done &&
        tvPin.visible && (
          <button
            type="button"
            className="cf-desk-hotspot"
            style={{ left: `${tvPin.xPct}%`, top: `${tvPin.yPct}%` }}
            title={t("desk.switchOnTvTitle")}
            onClick={(e) => {
              e.stopPropagation();
              setTvOn(true);
              setTask2Done(true);
            }}
          >
            <span className="cf-desk-hotspot__dot" />
            <span className="cf-desk-hotspot__label">{t("desk.switchOnTv")}</span>
          </button>
        )}

      {/* Episode 2's desk package: a real 3D box + "Click to open" sticky note
          (DeskPackage.tsx) that stays on the desk. This dot is glued to the
          sticky note's screen position and only appears during the decision
          beat, before it's been opened — clicking it reveals the full
          UsbDevice choice card (contents were never a floating 2D card). */}
      {sim.phase === "running" &&
        beat?.id === "b_usb_decision" &&
        !packageOpened &&
        packagePin.visible && (
          <button
            type="button"
            className="cf-desk-hotspot cf-package-hotspot"
            style={{ left: `${packagePin.xPct}%`, top: `${packagePin.yPct}%` }}
            title={t("desk.openPackageTitle")}
            onClick={(e) => {
              e.stopPropagation();
              audio.packageUnwrap();
              setPackageOpened(true);
            }}
          >
            <span className="cf-desk-hotspot__dot" />
            <span className="cf-desk-hotspot__label">{t("desk.clickToOpen")}</span>
          </button>
        )}

      {sim.phase === "running" && deskPcOpen && activeEnv.deskPc && (
        <PcScreenOverlay
          spec={activeEnv.deskPc}
          onClose={() => {
            setDeskPcOpen(false);
            setDeskPcStage("login");
          }}
          onStage={setDeskPcStage}
          onBrowserOpen={() => setTask1Done(true)}
        />
      )}

      {/* Walkable-mode task notes (top-right, always on): tells the learner what
          to do next; during the PC login it carries the copyable password so it
          can be pasted instead of typed. Sits ABOVE the PC overlay. */}
      {sim.phase === "running" &&
        isWalkablePoc &&
        !showGltfLoader &&
        beat?.type !== "intro" &&
        activeEnv.deskPc &&
        (() => {
          const pw = activeEnv.deskPc.password ?? "Summer2024!";
          type Note = {
            step: string;
            title: string;
            body: string;
            copies?: { label: string; value: string }[];
          };
          let note: Note;
          if (deskPcOpen) {
            // Inside the PC zoom: guide the login → open-browser sub-steps.
            note =
              deskPcStage === "login"
                ? {
                    step: "Task 1 of 2",
                    title: "Sign in to the PC",
                    body:
                      "Type the password from the sticky note into the login screen \u2014 " +
                      "or copy it below and paste it into the password field.",
                    copies: [{ label: "Password", value: pw }],
                  }
                : {
                    step: "Task 1 of 2",
                    title: task1Done ? "Task 1 complete" : "Open the browser",
                    body: task1Done
                      ? "Nice \u2014 you opened the browser. Press Esc to go back to the " +
                        "room, then head to the conference room for task 2."
                      : "Open the browser (Edge) from the desktop icons or the taskbar to " +
                        "finish task 1.",
                  };
          } else if (!task1Done) {
            // Storyboard task 1, in the room.
            note = {
              step: "Task 1 of 2",
              title: "Sign in and open the browser",
              body:
                "Walk to the desk, read the yellow sticky note, sign in to the PC and " +
                "open the browser. Use \u201CSign in to the PC\u201D in the control box, or " +
                "look at the sticky note and click.",
            };
          } else if (!task2Done) {
            // Storyboard task 2.
            note = {
              step: "Task 2 of 2",
              title: "Switch on the conference TV",
              body:
                "Task 1 done. Now walk to the conference room (west end, past the meeting " +
                "table) and switch on the big wall TV \u2014 use \u201CSwitch on the TV\u201D " +
                "in the control box, or look at the TV and click.",
            };
          } else {
            note = {
              step: "All done",
              title: "Storyboard complete",
              body:
                "Both tasks done \u2014 the TV is showing the test image. This proves the " +
                "multi-room, multi-task storyboard flow end to end.",
            };
          }
          return (
            <GuideTooltip
              step={note.step}
              title={note.title}
              body={note.body}
              copies={note.copies}
              className="cf-walk-guide"
              showVoice={false}
            />
          );
        })()}

      {sim.phase === "running" &&
        !isFreeRoamPoc &&
        !isWalkablePoc &&
        isAlwaysLookPano &&
        scenario.roomWaypoints &&
        scenario.roomWaypoints.length > 0 &&
        beat?.type !== "intro" && (
          <div className="cf-freeroam-hint" role="status">
            {tx("Drag to look around · use Look at for fixed views")}
          </div>
        )}

      {/* Accessibility captions: mirrors every audio.speak() line on screen,
          regardless of the sound toggle, so the course works for hearing-
          impaired learners in every scene that has voice. Suppressed for
          device kinds that already render their OWN dialogue/transcript UI
          (itcall/publiccall's `.itc-convo`, techrequest's `.tr-lines`,
          terminal's `.term-lines`) and for `consequence` beats (own
          `.cq-lines` card) — otherwise the same line shows twice/three times
          at once. Devices with no dialogue UI of their own (kiosk, tablet,
          PC, charger...) still need this as their ONLY hearing-impaired
          affordance. */}
      {sim.sliceDone && returnScenarioId() && (
        <div className="cf-return-hub">
          <div className="card">
            <h2>{tx("Scene complete")}</h2>
            <p>{tx("You can go back to the terminal and choose another exercise.")}</p>
            <button
              type="button"
              className="btn"
              onClick={() => {
                const id = returnScenarioId();
                if (id) window.location.href = hubBackHref(id);
              }}
            >
              {tx("Back to the terminal")}
            </button>
          </div>
        </div>
      )}

      {returnScenarioId() && sliceStopIds().size === 0 && (
        <button
          type="button"
          className="cf-hub-back"
          onClick={() => {
            const id = returnScenarioId();
            if (id) window.location.href = hubBackHref(id);
          }}
        >
          {tx("Back to the terminal")}
        </button>
      )}

      {sim.phase === "running" &&
        caption &&
        beat?.type !== "consequence" &&
        beat?.type !== "terminal_debrief" &&
        !(beat?.type === "device" && HAS_OWN_DIALOGUE_UI.has(device?.kind ?? "")) &&
        scenario.id !== "spear-phishing-osint" &&
        scenario.id !== "phishing-office" &&
        // these courses show every spoken line in the guide card's voice strip
        scenario.id !== "quishing-restaurant" &&
        scenario.id !== "ransomware-office" &&
        scenario.id !== "one-click-too-many" &&
        scenario.id !== "the-1700-release" &&
        scenario.id !== "invoice-that-changed" && (
          <div className="cf-caption-bar" role="status" aria-live="polite">
            {caption}
          </div>
        )}

      {sim.phase === "running" && (
        <ReplayBanner
          whatIf={sim.whatIf}
          beat={beat}
          deviceKind={beat?.type === "device" ? device?.kind : undefined}
          deviceReplay={beat?.type === "device" && !!(device?.config as { replay?: boolean } | undefined)?.replay}
        />
      )}

      {/* The 17:00 Release: course map (route + chapter jumps), outside the monitor glass. */}
      {scenario?.id === "the-1700-release" && <DevDayMap />}

      {/* Inspect control — hidden on pack (look-around is always on there). */}
      {sim.phase === "running" &&
        beat &&
        !isPackPano &&
        !isAlwaysLookPano &&
        !freeExplore &&
        (beat.inspectAllowed || beat.inspectRequired) &&
        beat.type !== "intro" &&
        beat.type !== "title" &&
        beat.type !== "quiz" &&
        beat.type !== "debrief" &&
        beat.type !== "ending" &&
        beat.type !== "terminal_debrief" && (
          <button
            type="button"
            className={`inspect-btn ${inspectMode ? "on" : ""}`}
            onClick={() => {
              if (!inspectMode && beat.id) {
                setInspectedBeatIds((s) => new Set(s).add(beat.id));
              }
              setInspectMode((v) => !v);
            }}
          >
            {inspectMode ? tx("Resume") : tx("Look around")}
          </button>
        )}

      {/* Persistent bottom control unit: switch guided/free movement anytime,
          toggle background MUSIC (independent of voice), and re-open the
          navigation help. Hidden on the framed splash/briefing/quiz/debrief. */}
      {sim.phase === "running" &&
        beat &&
        beat.type !== "intro" &&
        beat.type !== "quiz" &&
        beat.type !== "debrief" &&
        beat.type !== "ending" &&
        beat.type !== "terminal_debrief" && (
          <div className="cf-mini-controls" role="group" aria-label={tx("Controls")}>
            {!isWalkablePoc && !!scenario?.allowFreeExplore && !inspectMode && (
              <button
                type="button"
                className={`cf-mc-btn ${freeExplore ? "on" : ""}`}
                onClick={() => chooseMoveMode(!freeExplore)}
                title={freeExplore ? tx("Switch back to the guided tour") : tx("Walk around freely")}
              >
                <span className="cf-mc-ico" aria-hidden>{freeExplore ? "\u{1F3AC}" : "\u{1F6B6}"}</span>
                {freeExplore ? tx("Guided tour") : tx("Free movement")}
              </button>
            )}
            <button
              type="button"
              className={`cf-mc-btn cf-mc-btn--icon ${musicOn ? "on" : ""}`}
              aria-pressed={musicOn}
              onClick={() => {
                const next = !musicOn;
                setMusicOn(next);
                audio.setMusicEnabled(next);
              }}
              title={musicOn ? tx("Turn background music off") : tx("Turn background music on")}
            >
              <span className="cf-mc-ico" aria-hidden>{musicOn ? "\u{1F3B5}" : "\u{1F507}"}</span>
              <span className="cf-mc-label">{musicOn ? t("music.on") : t("music.off")}</span>
            </button>
            <button
              type="button"
              className={`cf-mc-btn cf-mc-btn--icon ${voiceOn ? "on" : ""}`}
              aria-pressed={voiceOn}
              onClick={() => {
                const next = !voiceOn;
                setVoiceOn(next);
                audio.setVoiceEnabled(next);
              }}
              title={voiceOn ? tx("Turn the narrator off") : tx("Turn the narrator on")}
            >
              <span className="cf-mc-ico" aria-hidden>{"\u{1F5E3}"}</span>
              <span className="cf-mc-label">{voiceOn ? t("voice.on") : t("voice.off")}</span>
            </button>
            {(isWalkablePoc || !!scenario?.allowFreeExplore) && (
              <button
                type="button"
                className="cf-mc-btn cf-mc-btn--icon"
                onClick={() => setShowTutorial(true)}
                title={t("nav.howToMoveTitle")}
              >
                <span className="cf-mc-ico" aria-hidden>?</span>
                <span className="cf-mc-label">{t("nav.help")}</span>
              </button>
            )}
          </div>
        )}

      {/* Pack-in-3D-room: Marble pano look-around + projected pins + bag. */}
      {sim.phase === "running" && isPackPano && packCfg && (
          <PackRoom
            config={packCfg}
            pinScreens={packPinScreens}
            onComplete={(result) => {
              setPackFeedback(result);
              sim.recordOutcome({
                type: result.safe ? "refusal" : "disclosure",
                beatId: beat!.id,
                eventId: result.eventId,
                evidence: result.label,
              });
              sim.advance(result.outcome);
            }}
          />
        )}

      {/* Cafe charging: same Marble pano look-around + projected pins, single pick. */}
      {sim.phase === "running" && isChargePano && chargeCfg && (
        <CafeCharge
          config={chargeCfg}
          pinScreens={packPinScreens}
          onStageChange={setCafeStage}
          onRiskEvent={(result) => {
            sim.recordOutcome({
              type: "disclosure",
              beatId: beat!.id,
              eventId: result.eventId,
              evidence: result.label,
            });
          }}
          onComplete={(result) => {
            sim.recordOutcome({
              type: "refusal",
              beatId: beat!.id,
              eventId: result.eventId,
              evidence: result.label,
            });
            // The BadUSB compromise + the safe-choice explanation already taught the
            // lesson inline (staged flow), so skip the generic "risky" teach card.
            sim.advance("safe");
          }}
        />
      )}

      {/* Hotspots: screen-% anchors for non-pack beats only.
          Hide during Look around (orbit would leave them floating wrong). */}
      {sim.phase === "running" &&
        beat?.hotspots &&
        beat.hotspots.length > 0 &&
        !isPinPano &&
        !inspectMode && (
        <RoomHotspots hotspots={beat.hotspots} />
      )}

      {/* Start splash, only for a spec whose first beat is not an intro card
       *  (every built-in course starts on `b_intro` and skips this). */}
      {sim.phase === "ready" && new URLSearchParams(window.location.search).get("resume") !== "1" && (
        <div className="splash">
          <h1>{getHostConfig().customIntro?.title || scenario.title}</h1>
          <p>{getHostConfig().customIntro?.summary || scenario.topic}</p>
          <span className="tierbadge">
            {txr("graphics: {tier} · {reason}", { tier, reason: cap.reason })}
          </span>
          <QualityPrefs
            onChange={({ tier: t, reducedMotion }) => {
              if (t === null) {
                // "Auto": re-probe now that the override was just cleared, so
                // the badge/scene immediately reflect the real detected tier.
                probeCapability().then((c) => {
                  const merged = { ...c, reducedMotion: reducedMotion ?? c.reducedMotion };
                  setCap(merged);
                  setTier(merged.tier);
                });
                return;
              }
              setTier(t);
              setCap({ ...cap, tier: t, reducedMotion: reducedMotion ?? cap.reducedMotion });
            }}
          />
          {!!scenario.allowFreeExplore && (
            <div className="mode-choose" role="radiogroup" aria-label={tx("How do you want to move?")}>
              <div className="mode-choose__label">{t("mode.howExplore")}</div>
              <div className="mode-choose__opts">
                <button
                  type="button"
                  role="radio"
                  aria-checked={!freeExplore}
                  className={`mode-opt ${!freeExplore ? "on" : ""}`}
                  onClick={() => chooseMoveMode(false)}
                >
                  <span className="mode-opt__ico" aria-hidden>{"\u{1F3AC}"}</span>
                  <span className="mode-opt__t">{t("mode.guidedTour")}</span>
                  <span className="mode-opt__d">{t("mode.guidedTourDesc")}</span>
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={freeExplore}
                  className={`mode-opt ${freeExplore ? "on" : ""}`}
                  onClick={() => chooseMoveMode(true)}
                >
                  <span className="mode-opt__ico" aria-hidden>{"\u{1F6B6}"}</span>
                  <span className="mode-opt__t">{t("mode.freeMovement")}</span>
                  <span className="mode-opt__d">{t("mode.freeMovementDesc")}</span>
                </button>
              </div>
              <div className="mode-choose__hint">{t("mode.switchHint")}</div>
            </div>
          )}
          <button
            type="button"
            className={`sound-toggle ${soundOn ? "on" : ""}`}
            aria-pressed={soundOn}
            onClick={() => {
              const next = !soundOn;
              setSoundOn(next);
              audio.setEnabled(next);
            }}
          >
            {txr("<0>{v}</0>Sound & music: {v2}", { v: soundOn ? "\u{1F50A}" : "\u{1F507}", v2: soundOn ? tx("on") : tx("off") }, [<span className="sound-ico" />])}
          </button>
          <button
            className="btn"
            onClick={() => {
              audio.setEnabled(soundOn);
              audio.resume();
              // Walkable courses (e.g. the bar game) have an extra mission-briefing
              // card AFTER this screen with its own "Begin mission" CTA — starting
              // the ambient bed here means the learner hears bar chatter while still
              // reading settings, before the mission has "begun". Non-walkable
              // guided courses go straight from here into the first beat, so they
              // keep the original immediate start.
              if (soundOn && !scenario.walkable)
                audio.startAmbient(scenario.ambientUrl, scenario.ambientVolume, scenario.music ?? "office");
              sim.start();
            }}
          >
            {tx("Start the simulation")}
          </button>
        </div>
      )}

      {/* Intro beat: cinematic mission briefing (asymmetric glass card).
          Holiday Call keeps this same card open through the pool camera tour
          so narrative copy is not stuffed into the top-right guide. */}
      {/* The 17:00 Release: flat course-overview start screen (redesign of the
          intro card) with an optional "Read introduction" reader shown while the
          room downloads. Gated to this course so every other course is untouched. */}
      {COURSE_READINGS[scenario.id] && scenario.summary && beat?.type === "intro" && (() => {
        const reading = localizedReading(COURSE_READINGS[scenario.id]);
        const quizN = scenario.beats.find((b) => b.type === "quiz")?.quiz?.length ?? 0;
        const facts: OverviewFact[] = [];
        if (scenario.summary?.duration) facts.push({ icon: "clock", label: scenario.summary.duration });
        if (quizN > 0) facts.push({ icon: "quiz", label: tf("intro.quizQuestions", { n: quizN }) });
        facts.push({ icon: "sound", label: t("intro.need.sound") });
        const topics: OverviewTopic[] = (scenario.summary?.tags ?? []).map((tag) => ({ label: topicLabel(tag, uiLocale) }));
        const languages = SUPPORTED_LOCALES.map((l) => ({
          code: l.code,
          label: l.code.toUpperCase(),
          available: isLocaleAvailable(scenario.id, l.code),
        }));
        return (
          <div className="splash intro-splash">
            <div style={{ width: "min(1040px, 94vw)", height: "min(640px, 90vh)", display: "flex" }}>
              <StartHost
                courseTitle={reading.title}
                kicker={reading.kicker}
                situation={beat?.guide?.body ?? ""}
                goal={scenario.summary?.goal ?? ""}
                youDo={scenario.summary?.youDo ?? ""}
                facts={facts}
                topics={topics}
                languages={languages}
                currentLocale={uiLocale}
                onLocale={(code) => void handleLocaleChange(code)}
                slides={reading.slides}
                readMinutes={reading.readMinutes}
                ready={introSceneLoaded}
                loadedBytes={introSum.loaded}
                totalBytes={introSum.total}
                onStart={() => {
                  openTvPlaybackGate();
                  if (scenario.walkable && soundOn)
                    audio.startAmbient(scenario.ambientUrl, scenario.ambientVolume, scenario.music ?? "office");
                  sim.advance();
                }}
              />
            </div>
          </div>
        );
      })()}

      {(beat?.type === "intro" || isHolidayIntroCard) && !(COURSE_READINGS[scenario.id] && scenario.summary) && (
        <div className={`splash intro-splash${holidayIntroLive ? " is-live" : ""}`}>
          {beat?.id === "b_intro" && viewportW < 1440 && !lowResDismissed && (
            <div className="lowres-banner" role="status">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <path d="M12 3 2 20h20L12 3Z" />
                <path d="M12 10v4M12 17h.01" strokeLinecap="round" />
              </svg>
              <span>
                {tx("This experience is tuned for screens 1440px wide or larger. Some panels may look cramped at your current window size.")}
              </span>
              <button type="button" onClick={dismissLowResWarning} aria-label={tx("Dismiss")}>
                &times;
              </button>
            </div>
          )}
          <div className="intro-bloom" aria-hidden="true" />
          <div
            className={`intro-card${scenario.summary && beat?.id === scenario.initialBeatId && !holidayIntroLive ? " intro-card--summary" : ""}`}
          >
            {holidayIntroLive ? (
              <div className="intro-card__main">
                <div className="intro-kicker">
                  {scenario.beats.find((b) => b.id === "b_intro")?.kicker ?? t("intro.missionBriefing")}
                </div>
                <h1 className="intro-title">{beat?.guide?.title}</h1>
                <p className="intro-desc">{beat?.guide?.body}</p>
                {beat?.hold && (
                  <button type="button" className="cf-cta" onClick={() => sim.advance()}>
                    {tx("Continue")} <span aria-hidden>→</span>
                  </button>
                )}
              </div>
            ) : (
            <div className="intro-card__main">
              <div className="intro-kicker">{beat?.kicker ?? t("intro.missionBriefing")}</div>
              <h1 className="intro-title">
                {(beat?.id === scenario.initialBeatId && getHostConfig().customIntro?.title) || beat?.title}
              </h1>
              {scenario.summary && beat?.id === scenario.initialBeatId ? (
                <>
                  <div className="intro-sec">
                    <div className="intro-sec__label">{t("intro.situation")}</div>
                    <p className="intro-desc">{renderHighlighted(beat?.guide?.body ?? "", beat?.highlight)}</p>
                  </div>
                  <div className="intro-sec">
                    <div className="intro-sec__label">{t("intro.goal")}</div>
                    <p className="intro-sec__text">{scenario.summary.goal}</p>
                  </div>
                  <div className="intro-sec">
                    <div className="intro-sec__label">{t("intro.youDo")}</div>
                    <p className="intro-sec__text">{scenario.summary.youDo}</p>
                  </div>
                  <div className="intro-facts">
                    <span className="intro-facts__item">
                      <IntroGlyph kind="clock" className="intro-facts__ico" />
                      {scenario.summary.duration}
                    </span>
                    {(() => {
                      const n = scenario.beats.find((b) => b.type === "quiz")?.quiz?.length ?? 0;
                      return n > 0 ? (
                        <span className="intro-facts__item">
                          <IntroGlyph kind="quiz" className="intro-facts__ico" />
                          {t("intro.quizCount").replace("{n}", String(n))}
                        </span>
                      ) : null;
                    })()}
                    {(scenario.summary.needs ?? []).map((need) => (
                      <span key={need} className="intro-facts__item">
                        <IntroGlyph
                          kind={need === "sound" ? "sound" : need === "microphone_optional" ? "mic" : "keyboard"}
                          className="intro-facts__ico"
                        />
                        {t(`intro.need.${need}`)}
                      </span>
                    ))}
                  </div>
                  <div className="intro-topics">
                    <div className="intro-topics__label">{t("intro.topics")}</div>
                    <div className="intro-topics__row">
                      {scenario.summary.tags.map((tag) => (
                        <span key={tag} className="intro-topics__chip">
                          <IntroGlyph kind={topicGlyph(tag)} className="intro-topics__ico" />
                          {topicLabel(tag, uiLocale)}
                        </span>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <p className="intro-desc">{renderHighlighted(beat?.guide?.body ?? "", beat?.highlight)}</p>
              )}
              <div className="intro-lang" role="group" aria-label={tx("Course language")}>
                <svg className="intro-lang__globe" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M3 12h18M12 3c2.6 2.6 4 5.8 4 9s-1.4 6.4-4 9c-2.6-2.6-4-5.8-4-9s1.4-6.4 4-9Z" />
                </svg>
                {SUPPORTED_LOCALES.map((l) => {
                  const available = isLocaleAvailable(scenario.id, l.code);
                  return (
                    <button
                      key={l.code}
                      type="button"
                      className={`intro-lang__opt ${uiLocale === l.code ? "on" : ""}`}
                      disabled={!available}
                      title={available ? l.label : tx("{label} — not yet translated for this course", { label: l.label })}
                      onClick={() => void handleLocaleChange(l.code)}
                    >
                      {l.code.toUpperCase()}
                    </button>
                  );
                })}
              </div>
              {beat?.offerFreeRoamCheckbox && (
                <label className="intro-walkopt">
                  <input
                    type="checkbox"
                    checked={sim.walkOptIn}
                    onChange={(e) => sim.setWalkOptIn(e.target.checked)}
                  />
                  <span>
                    {tx("Let me walk around freely (W/A/S/D) — otherwise you'll look around from where you're standing, no walking required.")}
                  </span>
                </label>
              )}
              <button
                className={`cf-cta${introSceneLoaded ? "" : " cf-cta--waiting"}`}
                disabled={!introSceneLoaded}
                aria-disabled={!introSceneLoaded}
                title={introSceneLoaded ? undefined : t("intro.preparingRoom")}
                onClick={() => {
                  if (!introSceneLoaded) return;
                  // Real user gesture, right here — safe to unblock any
                  // atmospheric background video/TV audio that was queued
                  // silently while the learner was still on the briefing.
                  openTvPlaybackGate();
                  // Walkable courses defer the room's ambient bed to this exact
                  // moment too (see the "Start the simulation" handler above) —
                  // nothing should be audible before the mission actually starts.
                  if (scenario.walkable && soundOn)
                    audio.startAmbient(scenario.ambientUrl, scenario.ambientVolume, scenario.music ?? "office");
                  sim.advance();
                }}
              >
                {beat?.ctaLabel ?? t("intro.beginMission")}{" "}
                {introSceneLoaded ? (
                  <span aria-hidden>→</span>
                ) : (
                  <span className="cf-cta__spin" aria-hidden />
                )}
              </button>
              {!captureMode &&
                (needsGltfLoader ? (
                  <IntroLoadBar keys={requiredGltfUrls} ready={gltfReady} />
                ) : (
                  <IntroLoadBar keys={activeEnv.enablePano && activeEnv.panoUrl ? [activeEnv.panoUrl] : []} />
                ))}
            </div>
            )}
            <div className="intro-card__art" aria-hidden="true">
              <div className="intro-radar">
                <span className="intro-radar__ring" />
                <span className="intro-radar__ring" />
                <span className="intro-radar__ring" />
                <span className="intro-radar__sweep" />
              </div>
              {renderIntroIcon(beat?.icon ?? scenario.beats.find((b) => b.id === "b_intro")?.icon)}
              <div className="intro-grid" />
            </div>
          </div>
        </div>
      )}

      {/* Title beat: cinematic caption ("A few days later…") over the scene */}
      {beat?.type === "title" && (
        <div className="splash title-splash">
          <div className={`title-card${beat.kicker ? " title-card--panel" : ""}`} onClick={() => sim.advance()}>
            {beat.kicker && <span className="title-kicker">{beat.kicker}</span>}
            {beat.title && <h1>{beat.title}</h1>}
            {beat.guide?.body && <p>{beat.guide.body}</p>}
            {/* Title cards are clickable to skip the auto-advance timer, but
                nothing said so \u2014 users were stuck staring at it not knowing
                what to do (2026-07-25). */}
            <span className="title-tap-hint">{scenario && beat && sliceStopsNext(scenario, beat.id) ? tx("Back to the terminal") : t("title.tapToContinue")}</span>
          </div>
        </div>
      )}

      {/* Consequence beat: "what just happened" cinematic replay, shown before
          the teach card explains the risky choice (consequence-and-rewind). */}
      {beat?.type === "consequence" && <Consequence beat={beat} onContinue={() => sim.advance()} />}

      {/* Ending beat: Episode-12 branching finale (Attack Interrupted /
          Limited Breach / Major Incident). Renders through the SAME
          front-desk terminal kiosk chrome from Episode 1 (2026-07-26
          redesign) instead of a separate plain bulletin card — all three
          branches, retinted per outcome via Terminal's `tone` prop. Content
          (kicker/tag/title/lines/continueLabel) is unchanged, fully
          data-driven from the branch config. */}
      {beat?.type === "ending" &&
        endingCfg &&
        (() => {
          const band = endingBranchKey === "A" ? "good" : endingBranchKey === "B" ? "warn" : "bad";
          return (
            <Terminal
              key={beat.id}
              tone={band}
              tag={endingCfg.tag}
              kicker={endingCfg.kicker ?? tx("COMPANY SECURITY BULLETIN")}
              skipPowerOn
              config={{
                title: endingCfg.title,
                lines: endingCfg.lines,
                continueLabel: beat?.ending?.continueLabel ?? "Continue",
                voiceId: "onyx",
              }}
              onComplete={() => sim.advance()}
            />
          );
        })()}

      {/* Terminal debrief beat: full-course recap on the terminal kiosk,
          shown right before the quiz (see TerminalDebrief.tsx). */}
      {beat?.type === "terminal_debrief" && beat.terminalDebrief && (
        <TerminalDebrief key={beat.id} config={beat.terminalDebrief} onComplete={() => sim.advance()} />
      )}

      {/* Held camera / Marble explore: look around freely, then Continue.
          Kept outside .device-layer so it stays visible while inspectMode is on. */}
      {/* Look-at / walkable PoCs: stay in the room; no linear Continue. */}
      {beat?.hold &&
        !isHolidayIntroCard &&
        !isFreeRoamPoc &&
        !isWalkablePoc &&
        !(
          scenario.roomWaypoints &&
          scenario.roomWaypoints.length > 0 &&
          (scenario.scene.environments?.length ?? 0) === 0
        ) && (
        <button type="button" className="btn cf-hold-continue" onClick={() => sim.advance()}>
          {tx("Continue")}
        </button>
      )}

      {/* Selectable room waypoints → fade → jump (Marble multi-room tours). */}
      {sim.phase === "running" &&
        beat?.hold &&
        scenario.roomWaypoints &&
        scenario.roomWaypoints.length > 0 && (
          <RoomWaypointNav
            rooms={scenario.roomWaypoints}
            activeEnvironmentId={activeEnvId}
            activeBeatId={beat?.id}
            label={
              scenario.roomWaypoints.every((r) => r.environmentId === scenario.roomWaypoints![0].environmentId)
                ? tx("Look at")
                : tx("Go to")
            }
            disabled={roomFade}
            onSelect={(room) => {
              if (roomFade) return;
              const multiEnv = (scenario.scene.environments?.length ?? 0) > 0;
              const sameRoom =
                !multiEnv ||
                room.environmentId === activeEnvId ||
                (activeEnvId == null && room.environmentId === beat?.environmentId);
              if (sameRoom) {
                // Same-room look preset: jump beat + waypoint (pano re-aims).
                const dest = scenario.beats.find((b) => b.id === room.beatId);
                if (dest?.waypointId) {
                  handledCamBeat.current = null; // allow camera effect to re-bind
                  setCameraWaypointId(dest.waypointId);
                }
                useSim.getState().goToBeat(room.beatId);
                return;
              }
              // Cross-room hop: fade here, set env before beat so the env-cut
              // effect does not double-fade.
              setRoomFade(true);
              window.setTimeout(() => {
                setActiveEnvId(room.environmentId);
                const se = scenario.scene.environments?.find((e) => e.id === room.environmentId);
                if (se) audio.switchAmbient(se.ambientUrl, se.ambientVolume, se.music ?? "office");
                useSim.getState().goToBeat(room.beatId);
                window.setTimeout(() => setRoomFade(false), 300);
              }, 340);
            }}
          />
        )}
      <div className={`room-fade ${roomFade ? "on" : ""}`} aria-hidden={!roomFade} />
      {fadeCutKey && <div key={fadeCutKey} className="mfit-fadecut" aria-hidden="true" />}

      {/* Guide tooltip (non-intro beats with a guide). Pack-in-room has its own copy.
          Also suppressed while the AI assistant's own full-screen "before you
          start" briefing modal is open (aiStage === "briefing"): that modal
          lives inside `.device-layer`, a lower stacking context than `.guide`
          (see the note on `.device-layer` in styles.css), so no z-index on the
          modal can ever cover this tooltip — it must be hidden by stage. */}
      {beat &&
        beat.type !== "intro" &&
        beat.type !== "title" &&
        !isHolidayIntroCard &&
        !beat.newsCut &&
        !(
          beat.type === "device" &&
          device?.kind === "packlist" &&
          (device.config as PackListConfig).plateUrl
        ) &&
        !(device?.kind === "aiassistant" && aiStage === "briefing") &&
        !(device?.kind === "personallaptop" && laptopStage === "stealer") &&
        device?.kind !== "breachbrief" &&
        device?.kind !== "bankstory" &&
        device?.kind !== "slidedeck" &&
        device?.kind !== "privacyhunt" &&
        device?.kind !== "holidaybriefing" &&
        device?.kind !== "holidayfakehunt" &&
        device?.kind !== "holidayrecon" &&
        device?.kind !== "realorai" &&
        device?.kind !== "mfitclouddash" &&
        device?.kind !== "turkeyhunt" &&
        device?.kind !== "spotfraud" &&
        device?.kind !== "invaders" &&
        device?.kind !== "arcadetv" &&
        !(device?.kind === "holidaymeeting" && holidayStage !== null && holidayStage !== "invite") &&
        !(device?.kind === "holidaytablet" && holidayStage === "wifi_ssl_cert") &&
        beat.guide && (
        <GuideTooltip
          step={
            device?.kind === "malwarepc" && malwareGuide
              ? malwareGuide.step
              : device?.kind === "sendit" && senditGuide
              ? senditGuide.step
              : `${scenario.beats.findIndex((b) => b.id === beat.id) + 1} / ${scenario.beats.length}`
          }
          title={
            showPowerGate
              ? powerLabel
              : awareStage === "playing"
              ? ((device?.config as { awarenessVideo?: { headline?: string } }).awarenessVideo?.headline ?? tx("Watch the film"))
              : awareStage === "ended"
              ? tx("The film is finished")
              : beat?.type === "voice_call" && phonePhase === "code-ready"
              ? tx("Send the code")
              : device?.kind === "malwarepc" && malwareGuide
              ? malwareGuide.title
              : mfitGuideKind && mfitGuide
              ? mfitGuide.title
              : device?.kind === "sendit" && senditGuide
              ? senditGuide.title
              : device?.kind === "publicpc" && pcStage && PC_STAGE_GUIDE[pcStage]
              ? PC_STAGE_GUIDE[pcStage].title
              : device?.kind === "byodlaptop" && tabletStage && TABLET_STAGE_GUIDE[tabletStage]
              ? TABLET_STAGE_GUIDE[tabletStage].title
              : device?.kind === "cafecharge" && cafeStage && CAFECHARGE_STAGE_GUIDE[cafeStage]
              ? CAFECHARGE_STAGE_GUIDE[cafeStage].title
              : device?.kind === "personallaptop" && laptopStage && LAPTOP_STAGE_GUIDE[laptopStage]
              ? LAPTOP_STAGE_GUIDE[laptopStage].title
              : beat?.type === "voice_call" && (phonePhase === "talking" || phonePhase === "awaiting")
              ? tx("Enter the code")
              : device?.kind === "osintrecon" && osintStage && OSINT_STAGE_GUIDE[osintStage]
              ? OSINT_STAGE_GUIDE[osintStage].title
              : device?.kind === "inboxtriage" && inboxStage && INBOX_STAGE_GUIDE[inboxStage]
              ? INBOX_STAGE_GUIDE[inboxStage].title
              : device?.kind === "aiassistant" && aiStage && AI_ASSISTANT_STAGE_GUIDE[aiStage]
              ? AI_ASSISTANT_STAGE_GUIDE[aiStage].title
              : device?.kind === "barlaptop" && barLaptopStage && BARLAPTOP_STAGE_GUIDE[barLaptopStage]
              ? BARLAPTOP_STAGE_GUIDE[barLaptopStage].title
              : device?.kind === "holidaytablet" && holidayStage && HOLIDAY_TABLET_STAGE_GUIDE[holidayStage]
              ? HOLIDAY_TABLET_STAGE_GUIDE[holidayStage].title
              : device?.kind === "itcall" && itcPhase && ITCALL_STAGE_GUIDE[itcPhase]
              ? ITCALL_STAGE_GUIDE[itcPhase].title
              : device?.kind === "mitmreveal"
              ? (device.config as MitmRevealConfig).roomLabel ?? beat.guide.title
              : beat.guide.title
          }
          body={
            showPowerGate
              ? tx("The screen is off. Click {powerLabel}.", { powerLabel })
              : awareStage === "playing"
              ? tx("Click Continue to quiz whenever you are ready.")
              : awareStage === "ended"
              ? tx("Click Continue to quiz.")
              : beat?.type === "voice_call" && phonePhase === "code-ready"
              ? tx("The code is entered. Click Send code, or Hang up to refuse.")
              : device?.kind === "malwarepc" && malwareGuide
              ? malwareGuide.body
              : mfitGuideKind && mfitGuide
              ? mfitGuide.body
              : device?.kind === "sendit" && senditGuide
              ? senditGuide.body
              : device?.kind === "publicpc" && pcStage && PC_STAGE_GUIDE[pcStage]
              ? PC_STAGE_GUIDE[pcStage].body
              : device?.kind === "byodlaptop" && tabletStage && TABLET_STAGE_GUIDE[tabletStage]
              ? TABLET_STAGE_GUIDE[tabletStage].body
              : device?.kind === "cafecharge" && cafeStage && CAFECHARGE_STAGE_GUIDE[cafeStage]
              ? CAFECHARGE_STAGE_GUIDE[cafeStage].body
              : device?.kind === "personallaptop" && laptopStage && LAPTOP_STAGE_GUIDE[laptopStage]
              ? LAPTOP_STAGE_GUIDE[laptopStage].body
              : beat?.type === "voice_call" && (phonePhase === "talking" || phonePhase === "awaiting")
              ? tx("Enter the code on the keypad. Then click Send code. Or click Hang up to refuse.")
              : device?.kind === "osintrecon" && osintStage && OSINT_STAGE_GUIDE[osintStage]
              ? OSINT_STAGE_GUIDE[osintStage].body
              : device?.kind === "inboxtriage" && inboxStage && INBOX_STAGE_GUIDE[inboxStage]
              ? INBOX_STAGE_GUIDE[inboxStage].body
              : device?.kind === "aiassistant" && aiStage && AI_ASSISTANT_STAGE_GUIDE[aiStage]
              ? AI_ASSISTANT_STAGE_GUIDE[aiStage].body
              : device?.kind === "barlaptop" && barLaptopStage && BARLAPTOP_STAGE_GUIDE[barLaptopStage]
              ? BARLAPTOP_STAGE_GUIDE[barLaptopStage].body
              : device?.kind === "holidaytablet" && holidayStage && HOLIDAY_TABLET_STAGE_GUIDE[holidayStage]
              ? HOLIDAY_TABLET_STAGE_GUIDE[holidayStage].body
              : device?.kind === "itcall" && itcPhase && ITCALL_STAGE_GUIDE[itcPhase]
              ? ITCALL_STAGE_GUIDE[itcPhase].body
              : device?.kind === "mitmreveal"
              ? (device.config as MitmRevealConfig).narration[mitmLineIdx] ?? beat.guide.body
              : beat.guide.body
          }
          copies={showPowerGate ? undefined : beat.guide.copies}
          cta={showPowerGate ? undefined : beat.guide.cta}
          tip={
            device?.kind === "barlaptop" && barLaptopStage && BARLAPTOP_STAGE_GUIDE[barLaptopStage]?.tip
              ? BARLAPTOP_STAGE_GUIDE[barLaptopStage].tip
              : device?.kind === "holidaytablet" && holidayStage && HOLIDAY_TABLET_STAGE_GUIDE[holidayStage]?.tip
              ? HOLIDAY_TABLET_STAGE_GUIDE[holidayStage].tip
              : beat.guide.tip
          }
          actionLabels={(showPowerGate
            ? [powerLabel]
            : awareStage === "playing" || awareStage === "ended"
            ? ["Continue to quiz"]
            : device?.kind === "malwarepc" && malwareGuide?.actionLabel
            ? [malwareGuide.actionLabel]
            : mfitGuideKind && mfitGuide?.actionLabel
            ? [mfitGuide.actionLabel]
            : device?.kind === "sendit" && senditGuide?.actionLabel
            ? [senditGuide.actionLabel]
            : device?.kind === "itcall" && itcPhase && ITCALL_STAGE_GUIDE[itcPhase]
            ? labelsFrom(ITCALL_STAGE_GUIDE[itcPhase])
            : device?.kind === "publicpc" && pcStage && labelsFrom(PC_STAGE_GUIDE[pcStage]).length
            ? labelsFrom(PC_STAGE_GUIDE[pcStage])
            : guideActionLabels({
            kind: beat?.type === "voice_call" ? "phone" : device?.kind,
            holidayStage,
            osintStage,
            barLaptopStage,
            aiStage,
            phonePhase,
            beatLabel: beat.guide.actionLabel,
            beatLabels: beat.guide.actionLabels,
          }))}
          onAction={
            showPowerGate
              ? () => activateGuideAction(powerLabel)
              : device?.kind === "holidaytablet" && holidayStage && HOLIDAY_TABLET_STAGE_GUIDE[holidayStage]?.actionLabel
              ? () => window.dispatchEvent(new CustomEvent("cf-holiday-guide-action", { detail: { stage: holidayStage } }))
              : guideActionLabels({
                    kind: beat?.type === "voice_call" ? "phone" : device?.kind,
                    holidayStage,
                    osintStage,
                    barLaptopStage,
                    aiStage,
                    phonePhase,
                    beatLabel: beat.guide.actionLabel,
                    beatLabels: beat.guide.actionLabels,
                  }).length || showPowerGate || (mfitGuideKind && !!mfitGuide?.actionLabel) || (device?.kind === "sendit" && !!senditGuide?.actionLabel) || (device?.kind === "malwarepc" && !!malwareGuide?.actionLabel)
                ? (label) => {
                    const want = (label ?? "").trim().toLowerCase();
                    if (
                      (device?.kind === "newscast" || device?.kind === "newsexplainer") &&
                      want === ((device.config as { skipLabel?: string }).skipLabel ?? "").trim().toLowerCase()
                    ) {
                      audio.cancelSpeech();
                      sim.advance("skip");
                      return;
                    }
                    if (beat?.type === "camera" && beat.hold && activeEnv.counterHotspot && want === (activeEnv.counterHotspot.label ?? "Order a drink").trim().toLowerCase()) {
                      audio.click();
                      setGlobalTvDuck(0.42, 320);
                      useSim.getState().advance();
                      return;
                    }
                    activateGuideAction(label ?? "");
                  }
                : undefined
          }
          showVoice={!(beat.type === "device" && (HAS_OWN_DIALOGUE_UI.has(device?.kind ?? "") || device?.kind === "sendit" || device?.kind === "malwarepc"))}
          showTranscript={beat.type === "voice_call" || beat.type === "video_call"}
          transcriptLive={beat.type === "voice_call" ? phonePhase !== "ended" : true}
          className={device?.kind === "sendit" ? "guide--sendit" : undefined}
          whatIf={sim.whatIf ? (beat?.whatIfNote ?? sim.whatIf) : null}
          whatIfLabel={t("whatif.label")}
          altAction={
            beat.safeChoice && (beat.type !== "device" || screenOn || !SCREEN_KINDS.has(device?.kind ?? ""))
              ? { label: beat.safeChoice.label, orLabel: t("guide.or"), onClick: takeSafeChoice }
              : undefined
          }
        />
      )}

      {/* News-cut cinematic (breaking-news on the bar TV): the top-right guide
          card is suppressed above so it never covers the TV screen. Instead we
          show a small bottom caption + a "Skip ahead" button, letting the
          learner watch the footage unobstructed and move on before the ~9s
          auto-advance fires. */}
      {beat?.newsCut && (
        <div className="news-cut-bar">
          {beat.guide?.body && <span className="news-cut-caption">{beat.guide.body}</span>}
          <button type="button" className="btn news-cut-skip" onClick={() => sim.advance()}>
            {scenario && sliceStopsNext(scenario, beat.id) ? tx("Back to the terminal") : beat.guide?.cta ?? tx("Skip ahead")}
          </button>
        </div>
      )}

      {/* Device layer — hide while Looking around so the room (and markers) stay visible */}
      <div className="device-layer" hidden={inspectMode} aria-hidden={inspectMode}>
        {/* Power gate: the screen stays OFF (showing the dark 3D laptop screen)
            until the learner clicks to turn it on. Only shown on a device beat,
            which the camera flow guarantees is AFTER the zoom -> a click proves
            we're zoomed in at the desk. Once on, it stays on for later apps.
            office-under-attack (oua_desk) used to require clicking a hotspot
            glued to the monitor's real 3D sticky note instead of this button
            (2026-07-25 feedback: "remove sticky note: make login clickable" —
            clicking a tiny prop on the physical bezel wasn't discoverable and
            read as two different things fighting for the click). It now uses
            this SAME generic power button as every other course; the blinking
            frame around the password FIELD itself (see DeskLogin.tsx) is what
            tells the learner what to click next, once the screen is on. */}
        {showPowerGate && (
          <button
            type="button"
            className={`screen-power cf-blink-strong${ipadGlued ? " screen-power--glued" : ""}`}
            style={ipadGlueStyle}
            aria-label={powerLabel}
            onClick={() => {
              audio.powerOn();
              setScreenOn(true);
            }}
          >
            <span className="screen-power-ico" aria-hidden>
              <svg viewBox="0 0 24 24" width="100%" height="100%">
                <path
                  d="M12 3v9"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
                <path
                  d="M7.5 6.3a7 7 0 1 0 9 0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <span className="screen-power-label">{powerLabel}</span>
          </button>
        )}

        {/* Episode 10: the personal laptop's OWN sticky note (loungePin,
            projected live by three/LoungeLaptop) — same pattern as the desk
            monitor above, distinct pin because it's a different 3D prop. */}
        {isScreenApp && !screenOn && device?.kind === "personallaptop" && loungePin.visible && (
          <button
            type="button"
            className="cf-desk-hotspot cf-package-hotspot"
            style={{ left: `${loungePin.xPct}%`, top: `${loungePin.yPct}%` }}
            title={tx("Click the sticky note to open your laptop")}
            onClick={() => {
              audio.powerOn();
              setScreenOn(true);
            }}
          >
            <span className="cf-desk-hotspot__dot" />
            <span className="cf-desk-hotspot__label">{tx("This is your own laptop — click to open it")}</span>
          </button>
        )}

        {/* Bar Wi-Fi game: "Order a drink" click prompt near the bartender,
            ending the free-roam `b_walk` beat (same projected-pin pattern as
            loungePin, distinct 3D anchor — three/CounterHotspot.tsx). Only
            relevant on the hold camera beat itself; once the chat starts the
            environment no longer reports a counterHotspot pin update but the
            beat has already moved on regardless. */}
        {beat?.arcadeHub && activeEnv.arcadeSpots && (
          <HubPins
            spots={activeEnv.arcadeSpots}
            pins={arcadePins}
            onPick={(id) => {
              audio.click();
              useSim.getState().advance(id);
            }}
          />
        )}
        {beat?.type === "camera" && beat.hold && counterPin.visible && (
          <button
            type="button"
            className="cf-desk-hotspot cf-package-hotspot cf-order-hotspot"
            style={{ left: `${counterPin.xPct}%`, top: `${counterPin.yPct}%` }}
            title={activeEnv.counterHotspot?.label ?? tx("Order a drink")}
            onClick={() => {
              audio.click();
              setGlobalTvDuck(0.42, 320);
              useSim.getState().advance();
            }}
          >
            <span className="cf-desk-hotspot__dot" />
            <span className="cf-desk-hotspot__label">{activeEnv.counterHotspot?.label ?? tx("Order a drink")}</span>
          </button>
        )}

        {beat?.type === "device" && screenOn && device?.kind === "desktop" && (
          <Desktop
            config={device.config as DesktopConfig}
            highlightAppId={targetApp}
            toast={desktopToast}
            onOpen={(appId) => {
              if (appId === "__start") return; // Start menu is cosmetic in the PoC.
              sim.recordOutcome({ type: "page_opened", beatId: beat.id, meta: { app: appId } });
              if (beat.completeOn?.action === "open_app" && beat.completeOn.arg === appId) {
                sim.advance();
              }
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "mfitchat" && (screenOn || mfitIpad) && (
          <MfitChat
            key={beat.id}
            config={device.config as MfitChatConfig}
            glueStyle={mfitIpad ? (ipadGlued && screenBox?.visible ? ipadGlueStyle ?? null : null) : undefined}
            onGuideChange={setMfitGuide}
            onDone={mfitDone}
          />
        )}
        {beat?.type === "device" && device?.kind === "mfitconsent" && (
          <MfitConsent key={beat.id} config={device.config as MfitConsentConfig} onGuideChange={setMfitGuide} onDone={mfitDone} />
        )}
        {beat?.type === "device" && device?.kind === "mfitclouddash" && (
          <MfitCloudDash
            key={beat.id}
            config={device.config as MfitCloudDashConfig}
            whatIf={sim.whatIf ? (beat?.whatIfNote ?? sim.whatIf) : null}
            onDone={() => {
              const routes = (device.config as MfitCloudDashConfig).routes;
              const replay = !!useSim.getState().whatIf;
              if (routes?.length) {
                const hit = routes.find((r) => dashMatch(r, recordedEvents(), replay));
                sim.advance(hit?.key);
                return;
              }
              sim.advance(replay ? "replay" : undefined);
            }}
          />
        )}
        {beat?.type === "device" && screenOn && device?.kind === "mfapc" && (
          <MfaPc key={beat.id} config={device.config as MfaPcConfig} onGuideChange={setMfitGuide} onDone={mfitDone} />
        )}
        {beat?.type === "device" && device?.kind === "mfaphone" && (
          <MfaPhone key={beat.id} config={device.config as MfaPhoneConfig} onGuideChange={setMfitGuide} onDone={mfitDone} />
        )}
        {beat?.type === "device" && device?.kind === "clickfix" && (
          <ClickFix key={beat.id} config={device.config as ClickFixConfig} onGuideChange={setMfitGuide} onDone={mfitDone} />
        )}
        {beat?.type === "device" && device?.kind === "oneclick" && (
          <OneClick
            key={beat.id}
            config={device.config as OneClickConfig}
            screenBox={screenBox}
            onGuideChange={setMfitGuide}
            onDone={mfitDone}
            onRecord={(eventId, evidence) => mfitRecord([{ eventId, evidence }])}
          />
        )}
        {beat?.type === "device" && device?.kind === "invoice" && (
          <Invoice
            key={beat.id}
            config={device.config as InvoiceConfig}
            screenBox={screenBox}
            onGuideChange={setMfitGuide}
            onDone={mfitDone}
            onRecord={(eventId, evidence) => mfitRecord([{ eventId, evidence }])}
          />
        )}
        {beat?.type === "device" && device?.kind === "devday" && (
          <DevDay
            key={beat.id}
            config={device.config as DevDayConfig}
            screenBox={screenBox}
            onGuideChange={setMfitGuide}
            onDone={mfitDone}
            onRecord={(eventId, evidence) => mfitRecord([{ eventId, evidence }])}
          />
        )}
        {beat?.type === "device" && screenOn && device?.kind === "sendit" && (
          <SendItSafely onContinue={() => sim.advance()} onGuideChange={setSenditGuide} />
        )}
        {beat?.type === "device" && screenOn && device?.kind === "malwarepc" && (
          <MalwareDesk onContinue={() => sim.advance()} onGuideChange={setMalwareGuide} />
        )}
        {beat?.type === "device" && screenOn && device?.kind === "bankstory" && (
          <BankStory
            config={device.config as BankStoryConfig}
            onContinue={() => {
              if (beat.completeOn?.action === "continue") sim.advance();
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "policypage" && (
          <PolicyLibrary
            config={device.config as PolicyPageConfig}
            onContinue={() => {
              if (beat.completeOn?.action === "continue") sim.advance();
            }}
          />
        )}

        {beat?.type === "device" && screenOn && device?.kind === "mail" && (
          <Mail
            config={device.config as MailConfig}
            continueEmailId={
              beat.completeOn?.action === "read_email" ? beat.completeOn.arg : undefined
            }
            onContinue={(id) => {
              if (beat.completeOn?.action === "read_email" && beat.completeOn.arg === id) {
                sim.advance();
              }
            }}
            replyEmailId={
              beat.completeOn?.action === "reply" ? beat.completeOn.arg : undefined
            }
            onReply={(id) => {
              sim.recordOutcome({
                type: "disclosure",
                beatId: beat.id,
                eventId: "replied_to_lure",
                evidence: "replied to a too-good-to-be-true prize email",
              });
              if (beat.completeOn?.action === "reply" && beat.completeOn.arg === id) {
                sim.advance();
              }
            }}
            onOpenEmail={(id) => {
              // Opening shows the body; the learner reads, then Continue / Reply.
              sim.recordOutcome({ type: "page_opened", beatId: beat.id, meta: { email: id } });
            }}
            onClickLink={(id) => {
              sim.recordOutcome({ type: "page_opened", beatId: beat.id, meta: { clickedLink: id } });
              if (beat.completeOn?.action === "click_link" && beat.completeOn.arg === id) {
                sim.advance();
              }
            }}
            saveAttachmentEmailId={
              beat.completeOn?.action === "save_attachment" ? beat.completeOn.arg : undefined
            }
            onSaveAttachment={(emailId) => {
              sim.recordOutcome({
                type: "disclosure",
                beatId: beat.id,
                eventId: "saved_malicious_attachment",
                evidence: "saved an executable attachment from an unverified email",
              });
              if (beat.completeOn?.action === "save_attachment" && beat.completeOn.arg === emailId) {
                sim.advance();
              }
            }}
          />
        )}

        {beat?.type === "device" && screenOn && device?.kind === "browser" && (
          <Browser
            config={device.config as BrowserConfig}
            phase={
              (device.config as BrowserConfig).awarenessVideo
                ? "awareness"
                : beat.completeOn?.action === "download"
                ? "landing"
                : beat.completeOn?.action === "compromise"
                  ? "hijack"
                  : beat.completeOn?.action === "submit_form"
                    ? "login"
                    : "navigate"
            }
            onNavigate={(url) => {
              if (
                beat.completeOn?.action === "navigate" &&
                url.includes((device.config as BrowserConfig).targetUrl)
              ) {
                sim.advance();
              }
            }}
            onSubmit={(fields) => {
              sim.recordOutcome({
                type: "credentials_submitted",
                beatId: beat.id,
                evidence: fields.email,
              });
              // hijack flow advances via onHijackContinue after the disconnect screen;
              // login-only phishing advances on submit_form.
              if (beat.completeOn?.action === "submit_form") sim.advance();
            }}
            onDownload={() => {
              sim.recordOutcome({
                type: "page_opened",
                beatId: beat.id,
                meta: { downloaded: (device.config as BrowserConfig).landing?.fileName ?? "claim" },
              });
              if (beat.completeOn?.action === "download") sim.advance();
            }}
            onProceedInsecure={() => {
              sim.recordOutcome({
                type: "disclosure",
                beatId: beat.id,
                eventId: "ignored_cert_warning",
                evidence: "Proceed anyway on certificate warning",
              });
            }}
            onHijackContinue={() => {
              if (beat.completeOn?.action === "compromise") sim.advance();
            }}
            onBackToSafety={beat.safeChoice ? takeSafeChoice : undefined}
            onAware={setAwareStage}
            onAwarenessDone={() => {
              if (beat.completeOn?.action === "video_done") sim.advance();
            }}
          />
        )}

        {beat?.type === "device" && screenOn && device?.kind === "ebanking" && (
          <EBanking
            config={device.config as EBankingConfig}
            stage={beat.completeOn?.action === "submit_phone" ? "verify" : "dashboard"}
            onOpenNotification={() => {
              sim.recordOutcome({ type: "page_opened", beatId: beat.id, meta: { notification: true } });
              if (beat.completeOn?.action === "open_notification") sim.advance();
            }}
            onSubmitPhone={(value) => {
              sim.recordOutcome({ type: "credentials_submitted", beatId: beat.id, evidence: `phone:${value}` });
              if (beat.completeOn?.action === "submit_phone") sim.advance();
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "intercom" && (
          <Intercom
            config={device.config as IntercomConfig}
            onOpenDoor={() => {
              sim.recordOutcome({
                type: "disclosure",
                beatId: beat.id,
                eventId: "let_stranger_in",
                evidence: "Buzzed in an unverified visitor",
              });
              if (beat.completeOn?.action === "open_door") sim.advance("risky");
            }}
            onVerify={
              scenario?.edges.some((e) => e.from === beat.id && e.on === "safe")
                ? () => {
                    sim.recordOutcome({
                      type: "refusal",
                      beatId: beat.id,
                      eventId: "verified_visitor",
                      evidence: "Kept the door shut and called HQ to check the visitor",
                    });
                    sim.advance("safe");
                  }
                : undefined
            }
          />
        )}

        {beat?.type === "device" && device?.kind === "techrequest" && (
          <TechRequest
            config={device.config as TechRequestConfig}
            onAllow={() => {
              sim.recordOutcome({
                type: "disclosure",
                beatId: beat.id,
                eventId: "allowed_pc_access",
                evidence: "Let an unverified visitor operate the unlocked PC",
              });
              if (beat.completeOn?.action === "allow_check") sim.advance("risky");
            }}
            onDeny={
              scenario?.edges.some((e) => e.from === beat.id && e.on === "safe")
                ? () => {
                    sim.recordOutcome({
                      type: "refusal",
                      beatId: beat.id,
                      eventId: "refused_pc_access",
                      evidence: "Kept the visitor away from the PC and called IT",
                    });
                    sim.advance("safe");
                  }
                : undefined
            }
          />
        )}

        {beat?.type === "device" && device?.kind === "tailgate" && (
          <Tailgate
            key={beat.id}
            config={device.config as TailgateConfig}
            onEnded={(key) => {
              if (key === "risky") {
                sim.recordOutcome({
                  type: "disclosure",
                  beatId: beat.id,
                  eventId: "held_door_for_stranger",
                  evidence: "Held a badge-gated door open for an unverified visitor",
                });
              } else {
                sim.recordOutcome({
                  type: "refusal",
                  beatId: beat.id,
                  eventId: "redirected_visitor_to_reception",
                  evidence: "Directed the visitor to badge in at reception",
                });
              }
              sim.advance(key);
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "breachbrief" && (
          <BreachBrief
            key={beat.id}
            config={device.config as BreachBriefConfig}
            resumed={breachBriefSeen}
            onExit={(key) => {
              setBreachBriefSeen(true);
              sim.advance(key);
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "terminal" && (
          <Terminal
            key={beat.id}
            config={device.config as TerminalConfig}
            onPowerOn={() => setTerminalOn(true)}
            onComplete={() => sim.advance()}
            onChoose={(key) => sim.advance(key)}
          />
        )}

        {beat?.type === "device" && device?.kind === "usbdevice" && (!activeEnv.deskPackage || packageOpened) && (
          <UsbDevice
            key={beat.id}
            config={device.config as UsbDeviceConfig}
            onEnded={(key, _choiceId, eventId) => {
              if (eventId) {
                sim.recordOutcome({
                  type: key === "safe" || key === "reported" ? "refusal" : "disclosure",
                  beatId: beat.id,
                  eventId,
                  evidence:
                    (device!.config as UsbDeviceConfig).options.find((o) => o.id === _choiceId)?.label ??
                    eventId,
                });
              }
              sim.advance(key);
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "privacyhunt" && (
          <PrivacyHunt3D
            key={beat.id}
            config={device.config as PrivacyHuntConfig}
            items={activeEnv.privacyRoom?.items ?? []}
            pins={privacyPins}
            found={privacyFoundIds}
            onFound={(id) => setPrivacyFoundIds((prev) => new Set(prev).add(id))}
            onEnded={(foundIds) => {
              const total = activeEnv.privacyRoom?.items.length ?? foundIds.length;
              const thorough = total > 0 && foundIds.length / total >= 0.7;
              sim.recordOutcome({
                type: thorough ? "refusal" : "disclosure",
                beatId: beat.id,
                eventId: thorough ? "privacy_hunt_thorough" : "privacy_hunt_partial",
                evidence: `Found ${foundIds.length} of ${total} planted item(s) during the office scan`,
              });
              sim.advance();
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "privacyclassify" && (
          <PrivacySort
            key={beat.id}
            config={device.config as PrivacyClassifyConfig}
            items={activeEnv.privacyRoom?.items ?? []}
            onEnded={(result: PrivacyClassifyResult) => {
              const ratio = result.total ? result.correct / result.total : 0;
              sim.recordOutcome({
                type: ratio >= 0.7 ? "refusal" : "disclosure",
                beatId: beat.id,
                eventId: ratio >= 0.7 ? "privacy_sorted_well" : "privacy_sorted_poorly",
                evidence: `Correctly classified ${result.correct} of ${result.total} items`,
              });
              sim.advance(ratio >= 0.7 ? "safe" : "risky");
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "signin" && (
          <SignIn
            key={beat.id}
            config={device.config as SignInConfig}
            onEnded={(key, choiceId, eventId) => {
              if (eventId) {
                sim.recordOutcome({
                  type: key === "risky" ? "disclosure" : "refusal",
                  beatId: beat.id,
                  eventId,
                  evidence:
                    (device!.config as SignInConfig).options.find((o) => o.id === choiceId)?.label ?? eventId,
                });
              }
              sim.advance(key);
            }}
          />
        )}

        {beat?.type === "device" && screenOn && device?.kind === "desklogin" && (
          <DeskLogin
            key={beat.id}
            config={device.config as DeskLoginConfig}
            onSubmit={() => {
              sim.recordOutcome({ type: "page_opened", beatId: beat.id, meta: { app: "desklogin" } });
              sim.advance();
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "mfafatigue" && (
          <MfaFatigue
            key={beat.id}
            config={device.config as MfaFatigueConfig}
            onEnded={(key, choiceId, eventId) => {
              if (eventId) {
                sim.recordOutcome({
                  type: key === "risky" ? "disclosure" : "refusal",
                  beatId: beat.id,
                  eventId,
                  evidence:
                    (device!.config as MfaFatigueConfig).options.find((o) => o.id === choiceId)?.label ?? eventId,
                });
              }
              sim.advance(key);
            }}
          />
        )}

        {beat?.type === "device" && screenOn && device?.kind === "inboxtriage" && (
          <InboxTriage
            key={beat.id}
            config={device.config as InboxTriageConfig}
            onStageChange={setInboxStage}
            onSubmit={(result) => {
              for (const ev of result.events) {
                sim.recordOutcome({
                  type: ev.kind === "risk" ? "disclosure" : "refusal",
                  beatId: beat.id,
                  eventId: ev.eventId,
                  evidence: ev.evidence,
                });
              }
              sim.advance(result.overallKey === "poor" ? "risky" : "safe");
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "becverify" && (
          <BecVerify
            key={beat.id}
            config={device.config as BecVerifyConfig}
            onEnded={(key, choiceId, eventId) => {
              if (eventId) {
                sim.recordOutcome({
                  type: key === "risky" ? "disclosure" : "refusal",
                  beatId: beat.id,
                  eventId,
                  evidence:
                    (device!.config as BecVerifyConfig).options.find((o) => o.id === choiceId)?.label ?? eventId,
                });
              }
              sim.advance(key);
            }}
          />
        )}

        {beat?.type === "device" && screenOn && device?.kind === "aiassistant" && (
          <AiAssistant
            key={beat.id}
            config={device.config as AiAssistantConfig}
            onStageChange={setAiStage}
            onEnded={(result) => {
              // Build an id -> ScoringEvent lookup so each recorded outcome
              // carries the right kind (disclosure vs refusal) + label for the
              // evidence log; the exposure-map weight/dimension come from the
              // catalogue via eventId.
              const evMap = new Map<string, { kind: "risk" | "positive"; label: string }>();
              for (const va of Object.values(sim.scenario?.voiceAttackers ?? {})) {
                for (const ev of va.scoringEvents) evMap.set(ev.id, { kind: ev.kind, label: ev.label });
              }
              for (const id of result.eventIds) {
                const ev = evMap.get(id);
                sim.recordOutcome({
                  type: ev?.kind === "risk" ? "disclosure" : "refusal",
                  beatId: beat.id,
                  eventId: id,
                  evidence: ev?.label ?? id,
                });
              }
              sim.advance(result.outcomeKey);
            }}
          />
        )}

        {beat?.type === "device" && screenOn && device?.kind === "personallaptop" && (
          <PersonalLaptop
            key={beat.id}
            config={device.config as PersonalLaptopConfig}
            beatId={beat.id}
            onStageChange={setLaptopStage}
            onComplete={(key) => sim.advance(key)}
          />
        )}

        {beat?.type === "device" && device?.kind === "incidentticket" && (
          <IncidentTicket
            key={beat.id}
            config={device.config as IncidentTicketConfig}
            onEnded={(key, optionId, eventId) => {
              const cfg = device!.config as IncidentTicketConfig;
              const opt = cfg.options.find((o) => o.id === optionId);
              if (key === "misfiled") {
                sim.recordOutcome({
                  type: "disclosure",
                  beatId: beat.id,
                  eventId: "incident_category_wrong",
                  evidence: "Filed the right description under the wrong category.",
                });
              } else if (eventId) {
                sim.recordOutcome({
                  type: key === "risky" ? "disclosure" : "refusal",
                  beatId: beat.id,
                  eventId,
                  evidence: opt?.shortLabel ?? "Filed the incident ticket.",
                });
              }
              sim.advance(key);
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "incidentreport" && (
          <IncidentReport
            key={beat.id}
            config={device.config as IncidentReportConfig}
            evidence={sim.evidence()}
            onSubmit={() => {
              const cfg = device!.config as IncidentReportConfig;
              sim.recordOutcome({
                type: "refusal",
                beatId: beat.id,
                eventId: cfg.submitEventId,
                evidence: "Submitted a full written incident report to IT/Security.",
              });
              sim.advance();
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "usb" && (
          <UsbReveal
            config={device.config as UsbConfig}
            onContinue={() => {
              if (beat.completeOn?.action === "continue") sim.advance();
              else sim.advance();
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "quishing" && (
          <QuishingPhone
            config={device.config as QuishingConfig}
            stage={
              beat.completeOn?.action === "grant_permissions"
                ? "permissions"
                : beat.completeOn?.action === "browse_menu"
                ? "capture"
                : "scan"
            }
            onGrant={(perm) => {
              sim.recordOutcome({
                type: "disclosure",
                beatId: beat.id,
                eventId: `granted_${perm}`,
                evidence: `Granted ${perm} permission to a typosquat site`,
              });
            }}
            onOpenSite={() => {
              sim.recordOutcome({
                type: "page_opened",
                beatId: beat.id,
                eventId: "opened_typosquat",
                evidence: "Opened a look-alike (typosquat) restaurant URL",
              });
            }}
            onDone={() => sim.advance()}
          />
        )}

        {beat?.type === "device" && device?.kind === "deepfakegen" && (
          <DeepfakeGen
            config={device.config as DeepfakeGenConfig}
            onContinue={() => sim.advance()}
          />
        )}

        {beat?.type === "device" && device?.kind === "deepfakecall" && (
          <DeepfakeCall
            config={device.config as DeepfakeCallConfig}
            beatId={beat.id}
            onEnded={(key) => sim.advance(key)}
          />
        )}

        {beat?.type === "device" && device?.kind === "itcall" && (
          <ItCall
            config={device.config as ItCallConfig}
            beatId={beat.id}
            onEnded={(key) => sim.advance(key)}
            onPhaseChange={setItcPhase}
          />
        )}

        {beat?.type === "device" && device?.kind === "livevishing" && (
          <LiveVishingCall
            config={device.config as LiveVishingConfig}
            attacker={scenario.voiceAttackers[(device.config as LiveVishingConfig).attackerId]}
            beatId={beat.id}
            onEnded={(key) => sim.advance(key)}
          />
        )}

        {beat?.type === "device" && device?.kind === "remotesupport" && (
          <RemoteSupportOverlay
            config={device.config as RemoteSupportConfig}
            onContinue={() => sim.advance()}
          />
        )}

        {beat?.type === "device" && screenOn && device?.kind === "fileexplorer" && (
          <FileExplorer
            config={device.config as FileExplorerConfig}
            onDecline={beat.safeChoice ? takeSafeChoice : undefined}
            onRun={(fileId) => {
              sim.recordOutcome({
                type: "disclosure",
                beatId: beat.id,
                eventId: "executed_attachment",
                evidence: "ran update.exe from an unverified email",
              });
              if (
                beat.completeOn?.action === "run_file" &&
                (!beat.completeOn.arg || beat.completeOn.arg === fileId)
              ) {
                sim.advance();
              } else if (beat.completeOn?.action !== "run_file") {
                sim.advance();
              }
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "ransomnote" && (
          <RansomNote
            config={device.config as RansomNoteConfig}
            onContinue={() => sim.advance()}
          />
        )}

        {beat?.type === "device" && device?.kind === "leaksite" && (
          <LeakSite
            config={device.config as LeakSiteConfig}
            onContinue={() => sim.advance()}
          />
        )}

        {beat?.type === "device" && device?.kind === "newscast" && (
          <Newscast
            config={device.config as NewscastConfig}
            onContinue={() => sim.advance()}
            onSkip={() => sim.advance("skip")}
          />
        )}

        {beat?.type === "device" && device?.kind === "newsexplainer" && (
          <NewsExplainer
            config={device.config as NewsExplainerConfig}
            onContinue={() => sim.advance()}
            onSkip={() => sim.advance("skip")}
          />
        )}

        {beat?.type === "device" && device?.kind === "slidedeck" && (
          <SlideDeck
            key={beat.id}
            config={device.config as SlideDeckConfig}
            onComplete={() => sim.advance()}
            onExit={(key) => sim.advance(key)}
          />
        )}

        {beat?.type === "device" && device?.kind === "decisiondrill" && (
          <DecisionDrill
            key={beat.id}
            config={device.config as DecisionDrillConfig}
            onComplete={(result) => {
              const cfg = device.config as DecisionDrillConfig;
              const eventId = result.passed ? cfg.passEventId : cfg.failEventId;
              if (eventId) {
                sim.recordOutcome({
                  type: result.passed ? "refusal" : "disclosure",
                  beatId: beat.id,
                  eventId,
                  evidence: `${result.correct} of ${result.total} workplace decisions`,
                });
              }
              sim.advance();
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "socialpost" && (
          <SocialPost
            config={device.config as SocialPostConfig}
            onPosted={() => {
              sim.recordOutcome({
                type: "disclosure",
                beatId: beat.id,
                eventId: "overshared_publicly",
                evidence: (device!.config as SocialPostConfig).platform,
              });
            }}
            onContinue={() => sim.advance()}
          />
        )}

        {beat?.type === "device" && device?.kind === "osintrecon" && (
          <OsintRecon
            config={device.config as OsintReconConfig}
            onStageChange={setOsintStage}
            onContinue={() => sim.advance()}
          />
        )}

        {beat?.type === "device" && device?.kind === "macrodoc" && (
          <MacroDoc
            config={device.config as MacroDocConfig}
            onEnableMacro={() => {
              sim.recordOutcome({
                type: "disclosure",
                beatId: beat.id,
                eventId: "enabled_macro",
                evidence: "Enable Content",
              });
            }}
            onContinue={() => sim.advance()}
          />
        )}

        {beat?.type === "device" && device?.kind === "exfil" && (
          <Exfil
            config={device.config as ExfilConfig}
            onContinue={() => sim.advance()}
          />
        )}

        {beat?.type === "device" &&
          device?.kind === "packlist" &&
          !(device.config as PackListConfig).plateUrl && (
          <PackList
            config={device.config as PackListConfig}
            onChoose={(o) => {
              if (o.eventId) {
                sim.recordOutcome({
                  type: o.safe ? "refusal" : "disclosure",
                  beatId: beat.id,
                  eventId: o.eventId,
                  evidence: o.label,
                });
              }
              sim.advance(o.outcome ?? (o.safe ? "safe" : "risky"));
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "wifipicker" && (
          <WifiPicker
            config={device.config as WifiPickerConfig}
            onChoose={(n) => {
              if (n.eventId) {
                sim.recordOutcome({
                  type: n.trusted ? "refusal" : "disclosure",
                  beatId: beat.id,
                  eventId: n.eventId,
                  evidence: n.ssid,
                });
              }
              sim.advance(n.outcome ?? (n.trusted ? "safe" : "risky"));
            }}
          />
        )}

        {/* Bar Wi-Fi game: bartender/stranger conversation cards. Full-viewport
            overlay (not mesh-blended — same treatment as leaksite/newscast). */}
        {beat?.type === "device" && device?.kind === "barchat" && (
          <BarChat
            config={device.config as BarChatConfig}
            onCameraCue={(wpId) => setCameraWaypointId(wpId)}
            onDone={() => {
              // 2026-07-29 feedback: bar chatter + the background TV drowned
              // out the narration for the rest of the course. Once the
              // learner leaves the bartender conversation, permanently duck
              // both (ambient bed + every background TV screen) so scripted
              // voice lines stay clearly audible from here on.
              audio.duckScene(0.3);
              sim.advance();
            }}
          />
        )}

        {beat?.type === "device" && screenOn && device?.kind === "pooltablet" && ipadGlued && (
          <PoolTablet
            config={device.config as PoolTabletConfig}
            onContinue={() => sim.advance()}
            style={ipadGlueStyle}
          />
        )}

        {beat?.type === "device" && screenOn && device?.kind === "holidaytablet" && (ipadGlued || captureMode) && (
          <HolidayTablet
            config={{
              ...(device.config as HolidayTabletConfig),
              ...(scenario && beat && sliceStopsNext(scenario, beat.id)
                ? { continueLabel: "Back to the terminal" }
                : {}),
            }}
            onStageChange={setHolidayStage}
            style={ipadGlueStyle}
            onComplete={(outcome) => {
              if (outcome === "accepted") {
                sim.recordOutcome({
                  type: "disclosure",
                  beatId: beat.id,
                  eventId: "accepted_unverified_connection",
                  evidence: "Accepted Daniel Kovacs's unverified LinkedIn connection",
                });
              } else if (outcome === "ignored") {
                sim.recordOutcome({
                  type: "refusal",
                  beatId: beat.id,
                  eventId: "ignored_unverified_connection",
                  evidence: "Ignored the unsolicited LinkedIn connection",
                });
              } else if (outcome === "connected") {
                sim.recordOutcome({
                  type: "page_opened",
                  beatId: beat.id,
                  eventId: "joined_open_hotel_wifi",
                  evidence: "Joined the open Hotel_Guest network",
                });
              }
              sim.advance(outcome);
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "holidaybriefing" && (
          <HolidayBriefing
            config={device.config as HolidayBriefingConfig}
            onStageChange={setHolidayStage}
            onDone={() => sim.advance()}
          />
        )}

        {beat?.type === "device" && device?.kind === "holidayfakehunt" && (
          <HolidayFakeHunt
            config={device.config as HolidayFakeHuntConfig}
            onStageChange={setHolidayStage}
            onDone={() => sim.advance()}
          />
        )}

        {beat?.type === "device" && device?.kind === "holidayrecon" && (screenOn || captureMode) && (ipadGlued || captureMode) && (
          <HolidayRecon
            config={device.config as HolidayReconConfig}
            onStageChange={setHolidayStage}
            style={ipadGlueStyle}
            onDone={() => sim.advance()}
          />
        )}

        {beat?.type === "device" && screenOn && device?.kind === "holidaymeeting" && (
          <HolidayMeeting
            config={device.config as HolidayMeetingConfig}
            onStageChange={setHolidayStage}
            style={ipadGlued && (holidayStage === null || holidayStage === "invite") ? ipadGlueStyle : undefined}
            onVerification={(method) => {
              sim.recordOutcome({
                type: "verified_caller",
                beatId: beat.id,
                eventId:
                  method === "callback"
                    ? "verified_marc_via_stored_number"
                    : "verified_payment_with_treasury",
                evidence:
                  method === "callback"
                    ? "Called Marc using his stored corporate number"
                    : "Opened the meeting-grid card — a convincing room can still be entirely generated",
              });
            }}
            onComplete={(outcome) => {
              const safe = outcome !== "approved";
              sim.recordOutcome({
                type: safe ? "simulation_passed" : "simulation_failed",
                beatId: beat.id,
                eventId:
                  outcome === "approved"
                    ? "approved_unverified_payment"
                    : outcome === "rejected"
                      ? "rejected_and_reported"
                      : "followed_payment_procedure",
                evidence:
                  outcome === "approved"
                    ? "Approved the unverified EUR 486,000 payment"
                    : outcome === "rejected"
                      ? "Rejected and reported the payment request"
                      : "Moved verification into the approved company payment procedure",
              });
              sim.advance(outcome);
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "realorai" && (
          <RealOrAi config={device.config as RealOrAiConfig} onDone={() => sim.advance()} />
        )}

        {/* Bar Wi-Fi game: the counter laptop's mini-app (mesh-blended onto its
            own screen via SCREEN_KINDS, same power-gate as personallaptop). */}
        {beat?.type === "device" && screenOn && device?.kind === "barlaptop" && (
          <BarLaptop
            config={device.config as BarLaptopConfig}
            onOutcome={(evt) => {
              sim.recordOutcome({
                type: evt.type,
                beatId: beat.id,
                eventId: evt.eventId,
                evidence: evt.evidence,
              });
            }}
            onEnded={() => sim.advance()}
            onStageChange={setBarLaptopStage}
            onSafeExit={beat.safeChoice ? takeSafeChoice : undefined}
          />
        )}

        {/* Bar Wi-Fi game: hacker-screen man-in-the-middle reveal. Full-viewport
            overlay pushed in on a hacker-room monitor (same treatment as the
            other attacker-side cinematics above). */}
        {beat?.type === "device" && device?.kind === "mitmreveal" && (
          <MitmReveal
            config={device.config as MitmRevealConfig}
            onDone={() => sim.advance()}
            onLineChange={setMitmLineIdx}
            screenBox={activeEnv.screenQuad ? screenBox : null}
          />
        )}
        {beat?.type === "device" && device?.kind === "turkeyhunt" && (
          <TurkeyHunt
            key={beat.id}
            config={device.config as TurkeyHuntConfig}
            onDone={() => sim.advance()}
            screenBox={activeEnv.screenQuad ? screenBox : null}
          />
        )}
        {beat?.type === "device" && device?.kind === "spotfraud" && (
          <SpotFraud
            key={beat.id}
            config={device.config as SpotFraudConfig}
            onDone={() => sim.advance()}
            screenBox={activeEnv.screenQuad ? screenBox : null}
          />
        )}
        {beat?.type === "device" && device?.kind === "invaders" && (
          <Invaders
            key={beat.id}
            config={device.config as InvadersConfig}
            onDone={() => sim.advance()}
            screenBox={activeEnv.screenQuad ? screenBox : null}
          />
        )}
        {beat?.type === "device" && device?.kind === "arcadetv" && (
          <ArcadeTv
            key={beat.id}
            config={device.config as ArcadeTvConfig}
            onDone={() => sim.advance()}
            screenBox={activeEnv.screenQuad ? screenBox : null}
          />
        )}

        {/* "The Free Cocktail QR Code" (scene 2): full-viewport phone overlay,
            stage picked from this beat's completeOn action (mirrors quishing's
            scan/permissions/capture 3-beat pattern). The decision beat owns its
            own internal peel/recap/what-if sub-flow and reports the final
            outcome key via onAdvance so the beat graph's edges route by it. */}
        {beat?.type === "device" && device?.kind === "cocktailqr" && (
          <CocktailQrPhone
            config={device.config as CocktailQrConfig}
            stage={
              beat.completeOn?.action === "ms_login"
                ? "mslogin"
                : beat.completeOn?.action === "decide"
                ? "decision"
                : "scan"
            }
            onOpenSite={() => {
              sim.recordOutcome({
                type: "page_opened",
                beatId: beat.id,
                eventId: "opened_cocktail_qr",
                evidence: "Opened the free-cocktail QR site",
              });
            }}
            onMsContinue={() => {
              sim.recordOutcome({
                type: "page_opened",
                beatId: beat.id,
                eventId: "viewed_ms_login_lure",
                evidence: "Viewed the fake Microsoft sign-in page",
              });
            }}
            onChoose={(opt: CocktailQrOption) => {
              sim.recordOutcome({
                type: opt.safe ? "refusal" : "credentials_submitted",
                beatId: beat.id,
                eventId: opt.outcome,
                evidence: opt.label,
              });
            }}
            onAdvance={(key) => {
              // Inline Yes/No decision on the fake MS login page (replaces the
              // old separate decision beat). Record the outcome BEFORE routing
              // so the debrief/score reflects the credential choice.
              if (key === "qr_yes") {
                sim.recordOutcome({
                  type: "credentials_submitted",
                  beatId: beat.id,
                  eventId: "qr_creds_submitted",
                  evidence: "Signed in with the work account on the QR phishing page",
                });
              } else if (key === "qr_no_curious" || key === "qr_no_skip") {
                sim.recordOutcome({
                  type: "refusal",
                  beatId: beat.id,
                  eventId: "qr_refused_creds",
                  evidence: "Refused to enter the work login on the QR page",
                });
              }
              sim.advance(key);
            }}
          />
        )}

        {/* "The Influencer" arc (item i): she posts a story beside the bar
            stool, full-viewport overlay (no 3D asset needed — happens on her
            own phone, not on any monitor). */}
        {beat?.type === "device" && device?.kind === "photostory" && (
          <PhotoStory
            config={device.config as PhotoStoryConfig}
            onDone={() => {
              sim.recordOutcome({
                type: "disclosure",
                beatId: beat.id,
                eventId: (device.config as PhotoStoryConfig).mode === "post" ? "posted_public_story" : "viewed_public_story",
                evidence:
                  (device.config as PhotoStoryConfig).mode === "post"
                    ? "Posted photos + hashtags publicly from the bar"
                    : "Browsed the same public post the next day",
              });
              sim.advance();
            }}
          />
        )}

        {/* "The Influencer" arc: hacker's SECOND look — scrolls the same
            public post, then zooms into one photo's background to spot the
            confidential document. Glued to the hacker room's own screenQuad,
            same mechanism as mitmreveal. */}
        {beat?.type === "device" && device?.kind === "hackerzoom" && (
          <HackerZoom
            config={device.config as HackerZoomConfig}
            onDone={() => {
              sim.recordOutcome({
                type: "disclosure",
                beatId: beat.id,
                eventId: "osint_confidential_doc_spotted",
                evidence: "Hacker spotted a confidential document visible in the background of a public photo",
              });
              sim.advance();
            }}
            screenBox={activeEnv.screenQuad ? screenBox : null}
          />
        )}

        {/* "The Influencer" arc: the spear-phishing email draft, same glue as
            hackerzoom above. */}
        {beat?.type === "device" && device?.kind === "emaildraft" && (
          <EmailDraft
            config={device.config as EmailDraftConfig}
            onDone={() => {
              sim.recordOutcome({
                type: "disclosure",
                beatId: beat.id,
                eventId: "spear_phishing_email_drafted",
                evidence: "Hacker drafted a targeted spear-phishing email using the leaked project detail",
              });
              sim.advance();
            }}
            screenBox={activeEnv.screenQuad ? screenBox : null}
          />
        )}

        {/* "The Influencer" arc finale: rewind minigame — edit the story
            before it actually posts. Full-viewport overlay (phone chrome). */}
        {beat?.type === "device" && device?.kind === "storyeditor" && (
          <StoryEditor
            config={device.config as StoryEditorConfig}
            onDone={() => {
              sim.recordOutcome({
                type: "disclosure",
                beatId: beat.id,
                eventId: "edited_story_before_posting",
                evidence: "Reviewed and edited the story's photos/hashtags before posting",
              });
              sim.advance();
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "byodlaptop" && (
          <ByodLaptop
            config={device.config as ByodLaptopConfig}
            onStageChange={setTabletStage}
            onChoose={(c) => {
              if (c.eventId) {
                sim.recordOutcome({
                  type: c.safe ? "refusal" : "disclosure",
                  beatId: beat.id,
                  eventId: c.eventId,
                  evidence: c.label,
                });
              }
              sim.advance(c.outcome ?? (c.safe ? "safe" : "risky"));
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "shouldersurf" && (
          <ShoulderSurf
            config={device.config as ShoulderSurfConfig}
            inspected={inspectedBeatIds.has(beat.id)}
            onChoose={(o) => {
              if (o.eventId) {
                sim.recordOutcome({
                  type: o.safe ? "refusal" : "disclosure",
                  beatId: beat.id,
                  eventId: o.eventId,
                  evidence: o.label,
                });
              }
              sim.advance(o.outcome ?? (o.safe ? "safe" : "risky"));
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "publiccall" && (
          <ColleagueCall
            config={device.config as PublicCallConfig}
            onChoose={(o) => {
              if (o.eventId) {
                sim.recordOutcome({
                  type: o.safe ? "refusal" : "disclosure",
                  beatId: beat.id,
                  eventId: o.eventId,
                  evidence: o.label,
                });
              }
              sim.advance(o.outcome ?? (o.safe ? "safe" : "risky"));
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "lostdevice" && (
          <LostDevice
            config={device.config as LostDeviceConfig}
            pinScreens={packPinScreens}
            onChoose={(o) => {
              if (o.eventId) {
                sim.recordOutcome({
                  type: o.safe ? "refusal" : "disclosure",
                  beatId: beat.id,
                  eventId: o.eventId,
                  evidence: o.label,
                });
              }
              sim.advance(o.outcome ?? (o.safe ? "safe" : "risky"));
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "chargerchoice" && (
          <ChargerChoice
            config={device.config as ChargerChoiceConfig}
            onChoose={(o) => {
              if (o.eventId) {
                sim.recordOutcome({
                  type: o.safe ? "refusal" : "disclosure",
                  beatId: beat.id,
                  eventId: o.eventId,
                  evidence: o.label,
                });
              }
              sim.advance(o.outcome ?? (o.safe ? "safe" : "risky"));
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "byodgate" && (
          <ByodGate
            config={device.config as ByodGateConfig}
            onChoose={(o) => {
              if (o.eventId) {
                sim.recordOutcome({
                  type: o.safe ? "refusal" : "disclosure",
                  beatId: beat.id,
                  eventId: o.eventId,
                  evidence: o.label,
                });
              }
              sim.advance(o.outcome ?? (o.safe ? "safe" : "risky"));
            }}
          />
        )}

        {beat?.type === "device" && device?.kind === "incidenttimeline" && (
          <IncidentTimeline
            config={{
              ...(device.config as IncidentTimelineConfig),
              ...(scenario && beat && sliceStopsNext(scenario, beat.id)
                ? { continueLabel: "Back to the terminal" }
                : {}),
            }}
            onDone={() => sim.advance()}
          />
        )}
      </div>

      {/* Teach overlay */}
      {beat?.type === "teach" && (
        <TeachOverlay
          body={beat.teach?.body ?? ""}
          spot={
            beat.teach?.spot && beat.teach.spot.length > 0
              ? beat.teach.spot
              : beat.teach?.redFlags && beat.teach.redFlags.length > 0
              ? beat.teach.redFlags
              : resolveRedFlags(scenario)
          }
          protect={beat.teach?.protect ?? []}
          remember={beat.teach?.remember}
          reviewItems={packReviewItems}
          outcome={beat.teach?.outcome}
          continueLabel={
            scenario && beat && sliceStopsNext(scenario, beat.id)
              ? beat.teach?.continueLabel
                ? tx("Back to the terminal")
                : tx("Understand — go back to terminal")
              : beat.teach?.continueLabel
          }
          altContinue={beat.teach?.altContinue}
          heading={beat.teach?.heading}
          onContinue={() => sim.advance()}
          onAltContinue={(key) => sim.advance(key)}
          whatIf={sim.whatIf ? (beat?.whatIfNote ?? sim.whatIf) : null}
          whatIfLabel={t("whatif.label")}
        />
      )}

      {/* Voice (phone) call */}
      {beat?.type === "voice_call" && (
        <PhoneCall
          config={getPhoneConfig(scenario)}
          attacker={scenario.voiceAttackers[beat.voiceAttackerId ?? "fraud_dept"]}
          beatId={beat.id}
          onPhaseChange={setPhonePhase}
          onEnded={(key) => {
            const skipQuiz = new URLSearchParams(window.location.search).get("skipQuiz") === "1";
            if (skipQuiz && scenario.id === "phishing-office") {
              sim.goToBeat("b_quiz");
              return;
            }
            sim.advance(key);
          }}
        />
      )}

      {/* Deepfake video call (branching) */}
      {beat?.type === "video_call" && (
        <VideoCall
          config={getVideoCallConfig(scenario, beat.deviceId)}
          attacker={
            scenario.voiceAttackers[beat.voiceAttackerId ?? Object.keys(scenario.voiceAttackers)[0]]
          }
          beatId={beat.id}
          onEnded={(key) => sim.advance(key)}
        />
      )}

      {/* Debrief + quiz — suppressed for free-roam / walkable PoCs UNLESS the
          scenario actually authors its own quiz/debrief beats (e.g. bar-walkable's
          "The Influencer" arc ends in a real quiz+debrief, unlike the pure
          walkable sandboxes that have no such beats and keep no ending at all). */}
      {!scenario.freeRoam &&
        (!scenario.walkable || scenario.beats.some((b) => b.type === "quiz" || b.type === "debrief")) &&
        (beat?.type === "debrief" || beat?.type === "quiz" || sim.phase === "done") && (
          beat?.type === "quiz" && returnScenarioId() && new URLSearchParams(window.location.search).get("skipQuiz") === "1" ? (
            <div className="cf-return-hub">
              <div className="card">
                {(() => {
                  const gaveCode = sim.outcomes.some(
                    (o) =>
                      !scenario.beats.find((b) => b.id === o.beatId)?.forced &&
                      (o.eventId === "read_otp" || (o.evidence ?? "").startsWith("gave code")),
                  );
                  const summary = hubReturnSummary(scenario.id, gaveCode);
                  return (
                    <>
                      <h2>{summary.title}</h2>
                      {summary.paragraphs.map((paragraph) => (
                        <p key={paragraph}>{paragraph}</p>
                      ))}
                    </>
                  );
                })()}
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    const id = returnScenarioId();
                    if (id) window.location.href = hubBackHref(id);
                  }}
                >
                  {tx("Back to the terminal")}
                </button>
              </div>
            </div>
          ) : (
            <Debrief />
          )
        )}
    </div>
  );
}

/** The active environment for the current scene cut (base, or a named alt). */

function returnScenarioId(): string | null {
  try {
    const id = new URLSearchParams(window.location.search).get("return");
    if (id && /^[a-z0-9-]{1,64}$/.test(id)) return id;
  } catch {
    /* ignore */
  }
  return null;
}

function resolveEnvironment(scenario: ScenarioSpec, envId: string | null): EnvironmentSpec {
  if (envId) {
    const e = scenario.scene.environments?.find((x) => x.id === envId);
    if (e) return e.environment;
  }
  return scenario.scene.environment;
}

function resolveRedFlags(scenario: ScenarioSpec): string[] {
  const browser = scenario.devices.find((d) => d.kind === "browser");
  const cfg = browser?.config as BrowserConfig | undefined;
  return cfg?.phishing?.redFlags ?? [];
}

function getPhoneConfig(scenario: ScenarioSpec): PhoneConfig {
  const phone = scenario.devices.find((d) => d.kind === "phone");
  return (phone?.config as PhoneConfig) ?? { callerName: "Unknown" };
}

function getVideoCallConfig(scenario: ScenarioSpec, deviceId?: string): VideoCallConfig {
  const dev =
    (deviceId && scenario.devices.find((d) => d.id === deviceId)) ||
    scenario.devices.find((d) => d.kind === "videocall");
  return (dev?.config as VideoCallConfig) ?? ({ callerName: "Unknown", payment: { amount: "", recipient: "" }, lines: {} } as VideoCallConfig);
}
